import Big from 'big.js';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import { IsGSASmartPay, Issuer } from 'smg/cc';
import * as countries from 'smg/countries';
import * as smgCarts from 'smg/customer-api/v2/carts';
import { GetLineItems, LineItemMap, TypeMap, SizeMap } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { CreateOrderPayments, CreditRefused } from 'smg/customer-api/orderpayments';
import { GetPageRequestID } from 'smg/customer-api/pagerequests';
import * as paymentoptions from 'smg/customer-api/paymentoptions';
import { ClearCache, GetLoginToken, Session } from 'smg/customer-api/session';
import * as shippingoptions from 'smg/customer-api/shippingoptions';
import { GetFirstAndLast } from 'smg/users';
import * as usertaxexemptions from 'smg/customer-api/usertaxexemptions';
import { ShowError } from 'smg/errors';
import { BlockPastDates, ReportValidity } from 'smg/form';
import { Phone } from 'smg/phone';
import * as blockchyp from 'smg/blockchyp';
import { MountStaffNudge } from 'smg/staff-nudge';
import * as template from 'smg/template';
import { Uploader } from 'smg/uploader/v2';
import { Debounce, DollarFormat, ParseDollars } from 'smg/util';
import QueryString from 'smg/querystring';
import * as carts from '../carts';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly staxOmniWebPaymentsToken: string;
}
declare const window: myWindow;

// Inline CSS applied to the iframe-internal <input>. Express runs Bootstrap 4
// with Roboto on the body; FormControlFieldStyle() would probe the host theme,
// but this explicit override keeps the BlockChyp iframe PAN/CVC matching the
// rest of the form-controls on this page (Roboto on BS4).
const expressCardFieldStyle = [
    'width: 100%',
    'height: calc(1.5em + 0.75rem)',
    'padding: 0.375rem 0.75rem',
    'font-family: Roboto, Arial, sans-serif',
    'font-size: 1rem',
    'line-height: 1.5',
    'color: #495057',
    'background-color: transparent',
    'border: 0',
    'box-sizing: border-box',
    'outline: none',
].join('; ');

// A reload after the store credit balance moved under a placed order carries this flag, so the credit says so.
const CREDIT_CHANGED_KEY = 'checkoutStoreCreditChanged';

// Store credit (#12307): 'editing' until Place order, 'waiting' while the cards ahead of it charge, and 'not-taken'
// once one of them failed.
type creditStatus = 'editing' | 'waiting' | 'not-taken';

declare interface storeCredit {
    balance: Big;
    on: boolean;
    // The customer typed an amount. Until then the credit follows the most it can be.
    touched: boolean;
    amount: Big;
    error: string;
    status: creditStatus;
    changed: boolean;
    idempotencyKey?: string;
    paymentOption?: models.PaymentOption;
}

declare interface splitFixPlan {
    input: HTMLInputElement;
    label: string;
    amount: Big;
    // The uncharged card blocks the fix removes, empty when setting one card is enough.
    remove: HTMLElement[];
}

declare interface paidPayment {
    label: string;
    amount: Big;
}

const cardBrands: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
};

function cardName(cardType: string | undefined, last4: string): string {
    const brand = cardBrands[(cardType ?? '').toLowerCase()] ?? 'Card';
    return `${brand} ending ${last4}`;
}

// signedIn=1 is one-shot: a reload must not show the confirmation or the Credit marks again.
function consumeSignedInParam(): boolean {
    const url = new URL(window.location.href);
    if (url.searchParams.get('signedIn') !== '1') {
        return false;
    }
    url.searchParams.delete('signedIn');
    history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    return true;
}

void main();

async function main() {
    const $spinner = document.getElementById('checkout-spinner') as HTMLElement;
    const $content = document.getElementById('checkout-content') as HTMLElement;

    // Pre-fetch line item maps
    void GetLineItems();

    // Handle query params: ?quote=, ?order=, ?cart= replace the current cart
    const quoteID = QueryString().get('quote');
    const reorderID = QueryString().get('order');
    const cartID = QueryString().get('cart');

    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            hideCartLink: true,
            showToast: false,
            newCart: true,
        }).catch(() => window.location.href = `${Relref('/cart')}?quote=${QueryString().get('quote') || ''}`);
    } else if (reorderID) {
        await carts.AddCartItem({ orderID: reorderID }, {
            promptToCheckout: false,
            hideCartLink: true,
            showToast: false,
            newCart: true,
        }).catch(() => window.location.href = `${Relref('/cart')}?quote=${QueryString().get('order') || ''}`);
    } else if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            newCart: true,
        });
    }

    // Load cart — redirect to /order/ if empty
    const cartResp = await carts.GetCart('cartItems,totals,pendingDepositOrders,resumableOrder').catch(() => undefined);
    const rawCartItems = cartResp?.cartItems;
    if (!cartResp || !rawCartItems?.length) {
        window.location.href = Relref('/order');
        return;
    }

    $spinner.remove();
    $content.classList.remove('d-none');

    const cartItems = models.CartItem.fromArray(rawCartItems);

    // ─── State ──────────────────────────────────────────
    let currentSubtotal = cartResp.totals.subtotal;
    let currentTax = cartResp.totals.taxAmount;
    let currentTaxRate = cartResp.totals.taxRate;
    let gsaSmartPayWarned = false;
    const gsaSmartPayAcknowledgedFirst6s = new Set<string>();
    let currentDiscount = Big(0);
    let currentAvailableDiscount = cartResp.totals.availableDiscount;
    let currentShipping = cartResp.totals.shipping;
    // Server-capped project deposit credit applied to this cart. The /orders
    // submit transfers the matching deposit orderpayments onto the new order
    // (transferPrepaidDepositPayments), so currentTotal already nets this out —
    // we surface it as a negative "Prepayment" line so the shown total equals
    // the charge. Re-capped client-side only when exemption drops the tax (see
    // effectivePrepayment).
    let currentPrepayment = cartResp.totals.prepayment;
    let currentTotal = cartResp.totals.total;
    // Tax-exempt selection mirror. Exempt drops tax from the total, which can
    // shrink the deposit credit cap below the server's taxed value.
    let exempt = false;
    const appliedPromo = ''; // TODO: restore to `let` when express has promo codes to share
    let splitMode = false;
    // Monotonic id source for split-payment card blocks. Never decrements
    // on remove, so the BlockChyp iframe mount ids that get baked into a
    // pending Mount instance can never collide with a later addCardBlock.
    // Display position ("Card 2", "Card 3") is derived independently from
    // DOM order — see relabelCardBlocks. card-block-1 is statically rendered.
    let nextCardBlockId = 1;
    let cartPollInterval: ReturnType<typeof setInterval> | undefined;
    let orderID = '';
    let orderKey = '';
    // Remaining balance of a cross-reload resumed order. The replay-mismatch
    // guard compares what was charged against what the page expected — on a
    // resumed order the server charges this balance (which includes tax), not
    // the cart preview total (which has no tax until an address is entered).
    let resumedOrderBalance: InstanceType<typeof Big> | undefined;
    let chargeSequenceActive = false;
    // A signed-in customer's store credit, when they have some. It goes toward the order on its own, for the lesser
    // of the balance and what the order owes, and the customer can lower it or not use it. Cards charge first, so a
    // declined card never ties credit to an unfinished order; a check, wire or purchase order cannot decline, so with
    // those the credit is taken first and the method records the rest.
    let credit: storeCredit | undefined;
    // Payments an earlier visit already made on the resumed order. Its balance already counts them.
    const resumedPaid: paidPayment[] = [];
    // Payments made on this page, keyed by card block id (or 'credit'), so a retry that replays one never counts it twice.
    const pagePaid = new Map<string, paidPayment>();
    let methodsHiddenByCredit = false;
    // Sign-in invitation (#11022): every guest sees it, the credit one when the quote's account has credit.
    const justSignedIn = consumeSignedInParam();
    let customerHasCredit = cartResp.totals.customerHasCredit;
    let confirmationDismissed = false;
    // Until the resume check below runs, the page cannot tell a fresh checkout from an existing order's.
    let signInInviteReady = false;

    // ─── DOM refs ───────────────────────────────────────
    // Shipping
    const $shippingName = document.getElementById('shipping-name') as HTMLInputElement;
    const $shippingCompany = document.getElementById('shipping-company') as HTMLInputElement;
    const $shippingCountry = document.getElementById('shipping-country') as HTMLSelectElement;
    const $intlDutiesNotice = document.getElementById('intl-duties-notice') as HTMLDivElement;
    const $shippingState = document.getElementById('shipping-state') as HTMLSelectElement;
    const $shippingAddress1 = document.getElementById('shipping-address1') as HTMLInputElement;
    const $shippingAddress2 = document.getElementById('shipping-address2') as HTMLInputElement;
    const $shippingCity = document.getElementById('shipping-city') as HTMLInputElement;
    const $shippingZip = document.getElementById('shipping-zip') as HTMLInputElement;

    // Contact
    const $phone = document.getElementById('contact-phone') as HTMLInputElement;
    const $email = document.getElementById('contact-email') as HTMLInputElement;

    // Tax exemption
    const $taxExemption = document.getElementById('tax-exemption') as HTMLSelectElement;
    const $exemptionFields = document.getElementById('exemption-fields') as HTMLElement;
    const $exemptNumber = document.getElementById('exempt-number') as HTMLInputElement;
    const $exemptState = document.getElementById('exempt-state') as HTMLSelectElement;
    const $exemptUploadZone = document.getElementById('exempt-upload-zone') as HTMLElement;
    const $exemptUploadFiles = document.getElementById('exempt-upload-files') as HTMLElement;
    const $exemptUploadFeedback = document.getElementById('exempt-upload-feedback') as HTMLElement;

    // Payment
    const $paymentCard = document.getElementById('payment-card') as HTMLElement;
    const $noPaymentNeeded = document.getElementById('no-payment-needed') as HTMLElement;
    const $paymentMethod = document.getElementById('payment-method') as HTMLSelectElement;
    const $billSame = document.getElementById('bill-same') as HTMLInputElement;
    const $billingFields = document.getElementById('billing-address-fields') as HTMLElement;
    const $billingName = document.getElementById('billing-name') as HTMLInputElement;
    const $billingCountry = document.getElementById('billing-country') as HTMLSelectElement;
    const $billingState = document.getElementById('billing-state') as HTMLSelectElement;
    const $billingAddress1 = document.getElementById('billing-address1') as HTMLInputElement;
    const $billingAddress2 = document.getElementById('billing-address2') as HTMLInputElement;
    const $billingCity = document.getElementById('billing-city') as HTMLInputElement;
    const $billingZip = document.getElementById('billing-zip') as HTMLInputElement;

    // CC — PAN/CVC live inside BlockChyp-served iframes (PCI SAQ A path);
    // each card block owns its own Mount instance keyed by block id so
    // split-payment forms don't cross-wire (source-filtered postMessage).
    const $fieldsCc = document.getElementById('fields-cc') as HTMLElement;
    const $fieldsCheck = document.getElementById('fields-check') as HTMLElement;
    const $fieldsWire = document.getElementById('fields-wire') as HTMLElement;
    const $fieldsPo = document.getElementById('fields-po') as HTMLElement;
    const $expMonth1 = document.getElementById('exp-month-1') as HTMLSelectElement;
    const $expYear1 = document.getElementById('exp-year-1') as HTMLSelectElement;
    const $cardAmt1 = document.getElementById('card-amt-1') as HTMLInputElement;
    const $splitBtn = document.getElementById('split-btn') as HTMLButtonElement;
    const $splitTally = document.getElementById('split-tally') as HTMLElement;
    const $splitTallyText = document.getElementById('split-tally-text') as HTMLElement;
    const $splitFix = document.getElementById('split-fix') as HTMLButtonElement;
    const $additionalCards = document.getElementById('additional-card-blocks') as HTMLElement;

    // Store credit
    const $paymentMethods = document.getElementById('payment-methods') as HTMLElement;
    const $credit = document.getElementById('store-credit') as HTMLElement;
    const $creditBalance = document.getElementById('store-credit-balance') as HTMLElement;
    const $creditChanged = document.getElementById('store-credit-changed') as HTMLElement;
    const $creditNotTaken = document.getElementById('store-credit-not-taken') as HTMLElement;
    const $creditUse = document.getElementById('store-credit-use') as HTMLInputElement;
    const $creditAmountGroup = document.getElementById('store-credit-amount-group') as HTMLElement;
    const $creditAmount = document.getElementById('store-credit-amount') as HTMLInputElement;
    const $creditError = document.getElementById('store-credit-error') as HTMLElement;
    const $creditMax = document.getElementById('store-credit-max') as HTMLElement;
    const $creditOutcome = document.getElementById('store-credit-outcome') as HTMLElement;
    const $creditApplied = document.getElementById('store-credit-applied') as HTMLElement;
    const $creditKept = document.getElementById('store-credit-kept') as HTMLElement;
    const $creditCovers = document.getElementById('store-credit-covers') as HTMLElement;
    const $creditRest = document.getElementById('store-credit-rest') as HTMLElement;
    const $poTotalRule = document.getElementById('po-total-rule') as HTMLElement;
    const poTotalRuleDefault = $poTotalRule.textContent ?? '';
    const $creditMark = document.getElementById('store-credit-mark') as HTMLElement;
    const $availableDiscountMark = document.getElementById('available-discount-mark') as HTMLElement;

    // Sign-in invitation
    const $signInInvite = document.getElementById('sign-in-invite') as HTMLElement;
    ($signInInvite.querySelector('.sign-in-invite-dismiss') as HTMLButtonElement).addEventListener('click', () => {
        confirmationDismissed = true;
        refreshSignInInvite();
    });

    // Per-block BlockChyp mount registry. Key = block id (e.g. "card-block-1").
    const cardMounts = new Map<string, blockchyp.Mount>();
    // Per-block onCardInput callback. Set after a decline so re-entering
    // PAN/CVC in the iframe re-enables the Retry button.
    const cardInputHandlers = new Map<string, () => void>();
    // Per-block tokenized card cache. Populated by tokenizeAllCardBlocks
    // BEFORE the /orders POST so empty/invalid PAN/CVC surfaces as a
    // validation error pre-order — not as a "decline" after the order
    // exists and the cart has been forgotten. Consumed (and cleared) by
    // chargeCardsSequentially so retry-after-decline re-tokenizes fresh.
    const cardTokens = new Map<string, blockchyp.TokenizedCard>();
    // Idempotency keys, one per intended charge attempt (card block / manual
    // type), minted on first use and reused across every retry of that attempt
    // so a retry after a lost response replays the recorded charge instead of
    // charging twice (#5656). Deliberately never reset between retry runs.
    const blockIdemKeys = new Map<string, string>();
    const manualIdemKeys = new Map<models.PaymentOptionTypeID, string>();

    // The order this cart becomes may be re-homed to the company that owns the
    // cart's source quote/order (see orders.go), and the BlockChyp tokenizing key
    // is per-merchant — so mount under the cart's resolved merchant, not the
    // browsing site's. No fallback: a mis-keyed token fails the exchange after the
    // order exists, which is unrecoverable. Lazily resolved and shared by every
    // Mount so mountCardBlock stays synchronous. Cleared on rejection (empty
    // token, tokenizing-key lookup failure, or iframe load failure) so a later
    // mount/retry re-resolves instead of reusing a dead promise.
    //
    // Once orderID is set (post-/orders), resolve by orderIDs instead of cart —
    // the cart is forgotten after create, and normalizeNewOrderRealmToSalesTeam
    // (#7537) may have re-homed the order to a different merchant than the cart
    // resolved (see merchant check before chargeCardsSequentially).
    let merchantToken: Promise<string> | undefined;
    const resolveMerchantToken = (): Promise<string> => {
        if (!merchantToken) {
            const merchantLookup = orderID ?
                paymentoptions.PaymentMerchant({ orderIDs: [orderID] }) :
                paymentoptions.PaymentMerchant({ cartID: smgCarts.GetCartID() || '' });
            const pending: Promise<string> = merchantLookup.
                then(merchant => {
                    if (!merchant.webPaymentsToken) {
                        throw new Error('This brand is missing its payment processor configuration, so the card cannot be entered. Please contact us to complete your order.');
                    }
                    return merchant.webPaymentsToken;
                });
            merchantToken = pending;
            pending.catch(() => {
                // Clear the cache only if it still holds THIS promise — Start
                // Over (or the parity remount) may already have replaced it
                // with a newer lookup, and a stale rejection clearing that one
                // would drop live token provenance and force a needless
                // re-enter-your-card cycle.
                if (merchantToken === pending) {
                    merchantToken = undefined;
                }
            });
        }
        return merchantToken;
    };

    // PO
    const $poNumber = document.getElementById('po-number') as HTMLInputElement;
    const $poUploadZone = document.getElementById('po-upload-zone') as HTMLElement;
    const $poUploadFiles = document.getElementById('po-upload-files') as HTMLElement;
    const $poUploadFeedback = document.getElementById('po-upload-feedback') as HTMLElement;

    // Section 5
    const $eventYes = document.getElementById('event-yes') as HTMLInputElement;
    const $eventNo = document.getElementById('event-no') as HTMLInputElement;
    const $eventDateField = document.getElementById('event-date-field') as HTMLElement;
    const $eventDate = document.getElementById('event-date') as HTMLInputElement;
    const revalidateEventDate = BlockPastDates($eventDate, { earliest: 'tomorrow' });
    const $verify = document.getElementById('verify') as HTMLInputElement;
    const $permMarketing = document.getElementById('perm-marketing') as HTMLInputElement;
    const $permSms = document.getElementById('perm-sms') as HTMLInputElement;

    // Summary
    const $summaryItems = document.getElementById('summary-items') as HTMLElement;
    const $summarySubtotal = document.getElementById('summary-subtotal') as HTMLElement;
    const $discountRow = document.getElementById('discount-row') as HTMLElement;
    const $availableDiscountRow = document.getElementById('available-discount-row') as HTMLElement;
    // TODO: restore when express has promo codes to share
    // const $promoBadge = document.getElementById('promo-badge') as HTMLElement;
    const $discountAmount = document.getElementById('discount-amount') as HTMLElement;
    const $availableDiscountAmount = document.getElementById('available-discount-amount') as HTMLElement;
    const $taxRateLabel = document.getElementById('tax-rate-label') as HTMLElement;
    const $taxSpinner = document.getElementById('tax-spinner') as HTMLElement;
    const $taxValue = document.getElementById('tax-value') as HTMLElement;
    const $prepaymentBlock = document.getElementById('prepayment-block') as HTMLElement;
    const $prepaymentList = document.getElementById('prepayment-list') as HTMLElement;
    const $prepaymentRow = document.getElementById('prepayment-row') as HTMLElement;
    const $prepaymentValue = document.getElementById('prepayment-value') as HTMLElement;
    const $orderTotal = document.getElementById('order-total') as HTMLElement;
    const $summaryPayments = document.getElementById('summary-payments') as HTMLElement;
    const $summaryPaidRows = document.getElementById('summary-paid-rows') as HTMLElement;
    const $summaryCreditRow = document.getElementById('summary-credit-row') as HTMLElement;
    const $summaryCreditBadge = document.getElementById('summary-credit-badge') as HTMLElement;
    const $summaryCreditAmount = document.getElementById('summary-credit-amount') as HTMLElement;
    const $summaryLeftAmount = document.getElementById('summary-left-amount') as HTMLElement;

    // TODO: restore when express has promo codes to share
    // const $promoInputGroup = document.getElementById('promo-input-group') as HTMLElement;
    // const $promoInput = document.getElementById('promo-input') as HTMLInputElement;
    // const $promoApplyBtn = document.getElementById('promo-apply-btn') as HTMLButtonElement;
    // const $promoApplied = document.getElementById('promo-applied') as HTMLElement;
    // const $appliedCode = document.getElementById('applied-code') as HTMLElement;
    // const $appliedDesc = document.getElementById('applied-desc') as HTMLElement;
    // const $promoRemoveBtn = document.getElementById('promo-remove-btn') as HTMLButtonElement;

    // Place order
    const $placeOrderBtn = document.getElementById('place-order-btn') as HTMLButtonElement;
    const $gsaSmartPayWarning = document.getElementById('gsa-smartpay-warning') as HTMLElement;
    const $gsaSmartPayTaxAmount = document.getElementById('gsa-smartpay-tax-amount') as HTMLElement;
    const $gsaSmartPayAddExemption = document.getElementById('gsa-smartpay-add-exemption') as HTMLAnchorElement;
    const $declineAlert = document.getElementById('decline-alert') as HTMLElement;
    const $checkoutForm = document.getElementById('checkout-form') as HTMLFormElement;

    // ─── Init: Phone ────────────────────────────────────
    const phoneInput = new Phone($phone);

    // ─── Init: Countries & States ───────────────────────
    const shippingSelectsReady = countries.Selects($shippingCountry, $shippingState, {
        defaultUnitedStates: true,
        phone: phoneInput,
        stateParentSelector: '.form-group',
    });

    void countries.Selects($billingCountry, $billingState, {
        defaultUnitedStates: true,
        stateParentSelector: '.form-group',
    });

    // Tax exemption state select (shares US states)
    void countries.Selects(
        // Create a temporary hidden country select pre-set to US so we can populate exempt states
        (() => {
            const tmp = document.createElement('select');
            tmp.style.display = 'none';
            document.body.appendChild(tmp);
            return tmp;
        })(),
        $exemptState,
        { defaultUnitedStates: true, stateParentSelector: '.form-group' },
    );

    $shippingCountry.addEventListener('change', () => {
        const countryID = parseInt($shippingCountry.value, 10);
        $intlDutiesNotice.classList.toggle('d-none', !countryID || countryID === models.CountryID.COUNTRIES_UNITED_STATES);
    });

    // ─── Init: Year Selects ─────────────────────────────
    populateYearSelect($expYear1);

    // ─── Init: Upload zones ─────────────────────────────
    const $exemptFileInput = $exemptUploadZone.querySelector('input[type="file"]') as HTMLInputElement;
    const exemptUploader = new Uploader({
        $input: $exemptFileInput,
        $dropzones: [$exemptUploadZone],
        $thumbnailsParent: $exemptUploadFiles,
    });
    $exemptUploadZone.addEventListener('click', () => $exemptFileInput.click());

    const $poFileInput = $poUploadZone.querySelector('input[type="file"]') as HTMLInputElement;
    const poUploader = new Uploader({
        $input: $poFileInput,
        $dropzones: [$poUploadZone],
        $thumbnailsParent: $poUploadFiles,
    });
    $poUploadZone.addEventListener('click', () => $poFileInput.click());

    // ─── Init: Prefill from session ─────────────────────
    // Session() keeps one GET /v2/session answer until ClearCache, and the store credit balance it reports is what this page spends.
    ClearCache();
    const creditChanged = window.sessionStorage.getItem(CREDIT_CHANGED_KEY) === '1';
    window.sessionStorage.removeItem(CREDIT_CHANGED_KEY);
    void Session().then(sess => {
        const balance = sess.user.id ? Big(sess.user.balance || 0) : Big(0);
        if (balance.gt(0)) {
            credit = { balance, on: true, touched: false, amount: Big(0), error: '', status: 'editing', changed: creditChanged };
            updateTotals();
        }
        return sess;
    }).then(sess => {
        if (sess.user.emailAddress.email && !$email.value) {
            $email.value = sess.user.emailAddress.email;
        }
        if (sess.user.phoneNumber.phone && !$phone.value) {
            void phoneInput.ready().then(() => phoneInput.iti.setNumber(sess.user.phoneNumber.phone));
        }
        if (sess.user.firstName && !$shippingName.value) {
            $shippingName.value = [sess.user.firstName, sess.user.lastName].filter(Boolean).join(' ');
        }
        if (sess.user.company && !$shippingCompany.value) {
            $shippingCompany.value = sess.user.company;
        }
    }).catch(() => { /* not logged in */ });

    // ─── Render: Summary Items ──────────────────────────
    await renderSummaryItems(cartItems);
    renderPrepaymentList(cartResp.pendingDepositOrders);
    updateTotals();

    // ─── Tax Lookup ─────────────────────────────────────
    const fetchTax = Debounce(async () => {
        const zip = $shippingZip.value.trim();
        const countryID = parseInt($shippingCountry.value, 10);
        const stateID = parseInt($shippingState.value, 10);
        const city = $shippingCity.value.trim();
        const address1 = $shippingAddress1.value.trim();

        // A frozen (resumed) form disables the select too, so disabled cannot mean "no states".
        const stateHidden = $shippingState.closest('.form-group')?.classList.contains('d-none') ?? false;
        // Tax is priced only once there is a street line; before that the server ignores the address.
        if (!address1 || !zip || !countryID || !city || (!stateHidden && !stateID)) {
            return;
        }

        setTaxState('loading');

        const taxAddress: models.ICartTotalsAddress = {
            addressLine1: address1,
            city,
            zip,
            countryID,
        };
        if (!stateHidden) {
            taxAddress.stateID = stateID;
        }

        try {
            // orderID is set by a fresh create and by resuming — "this checkout
            // pays an existing order" — so the preview must carry that order's
            // recorded available-discount spend, not the live balance it already consumed.
            const requestedOrderID = orderID;
            const resp = (await smgCarts.GetCart('totals', {
                shippingAddress: taxAddress,
                promoCode: appliedPromo || undefined,
                resuming: orderID !== '',
            }))?.totals;
            // Start Over / resume can flip orderID while a request is in
            // flight, and the stale response would repaint the wrong order's discount.
            if (orderID !== requestedOrderID) {
                return;
            }
            if (resp) {
                currentSubtotal = resp.subtotal;
                currentDiscount = resp.discount;
                currentAvailableDiscount = resp.availableDiscount;
                customerHasCredit = resp.customerHasCredit;
                currentShipping = resp.shipping;
                // Server re-caps the deposit credit against the freshly taxed
                // pre-total, and total already nets it out.
                currentPrepayment = resp.prepayment;
                // The summary belongs to the checkout regardless of exemption; only tax does.
                if ($taxExemption.value === 'none' || $taxExemption.value === '') {
                    currentTax = resp.taxAmount;
                    currentTaxRate = resp.taxRate;
                    currentTotal = resp.total;
                    setTaxState('loaded');
                    updateTotals();
                } else {
                    currentTax = Big(0);
                    currentTaxRate = new Big(0);
                    currentTotal = exemptTotal();
                    showExemptTax();
                    updateTotals();
                }
            }
        } catch {
            // Silently ignore — tax will be confirmed at processing
        }
    }, 500, false);

    $shippingAddress1.addEventListener('blur', () => fetchTax());
    $shippingZip.addEventListener('blur', () => fetchTax());
    $shippingZip.addEventListener('input', () => fetchTax());
    $shippingCity.addEventListener('blur', () => fetchTax());
    $shippingState.addEventListener('change', () => fetchTax());

    // ─── Tax Exemption Toggle ───────────────────────────
    $taxExemption.addEventListener('change', () => {
        const val = $taxExemption.value;
        const show = val !== 'none' && val !== '';
        $exemptionFields.classList.toggle('open', show);

        // Toggle required on inner fields
        $exemptNumber.required = show;
        $exemptState.required = show;

        if (show) {
            // Exempt — show $0.00 pending verification
            exempt = true;
            currentTax = Big(0);
            currentTaxRate = new Big(0);
            // Dropping tax shrinks the pre-prepayment total, so re-cap the
            // deposit credit against it (effectivePrepayment) and net it out so
            // the shown total still equals what the card is charged.
            currentTotal = exemptTotal();
            showExemptTax();
            $taxValue.style.display = '';
            updateTotals();
        } else {
            // Not exempt — recalculate with normal tax (server re-caps prepayment)
            exempt = false;
            fetchTax();
        }
    });

    $gsaSmartPayAddExemption.addEventListener('click', e => {
        e.preventDefault();
        if (!$taxExemption.value || $taxExemption.value === 'none') {
            $taxExemption.value = 'government';
            $taxExemption.dispatchEvent(new Event('change'));
        }
        $taxExemption.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    // ─── Payment Method Switching ───────────────────────
    const paymentPanels: Record<string, HTMLElement> = {
        cc: $fieldsCc,
        check: $fieldsCheck,
        wire: $fieldsWire,
        po: $fieldsPo,
    };

    $paymentMethod.addEventListener('change', () => {
        const method = $paymentMethod.value;
        for (const [key, panel] of Object.entries(paymentPanels)) {
            panel.classList.toggle('active', key === method);
        }

        if (method === 'cc') {
            // Mount BlockChyp iframes when CC panel becomes active. Block 1
            // is always present; additional blocks (split payment) own
            // their own mounts and are managed in addCardBlock/remove.
            mountCardBlock(document.getElementById('card-block-1') as HTMLElement);
            for (const block of $additionalCards.querySelectorAll<HTMLElement>('.payment-card-block')) {
                mountCardBlock(block);
            }
        } else {
            // Tear down all BlockChyp iframes when switching away from CC
            // so detached mounts don't keep listening to postMessage events.
            unloadAllCardBlocks();
            blockchyp.ClearMountFailure($fieldsCc);
            // If switching away from CC while split is active, reset
            if (splitMode) {
                resetSplitPayment();
            }
        }

        updateGSASmartPayWarning();
    });

    // ─── Billing Address Toggle ─────────────────────────
    $billSame.addEventListener('change', () => {
        const show = !$billSame.checked;
        $billingFields.classList.toggle('open', show);

        $billingName.required = show;
        $billingAddress1.required = show;
        $billingCity.required = show;
        $billingState.required = show;
        $billingZip.required = show;
        $billingCountry.required = show;

        if (!show) {
            // Clear billing fields when re-checked
            $billingName.value = '';
            $billingAddress1.value = '';
            $billingAddress2.value = '';
            $billingCity.value = '';
            $billingZip.value = '';
        }
    });

    // ─── BlockChyp mount: card-block-1 ──────────────────
    // The CC panel is the default-active payment method, so mount block 1
    // up-front. Subsequent payment-method changes are handled above.
    mountCardBlock(document.getElementById('card-block-1') as HTMLElement);

    // ─── Store Credit ───────────────────────────────────
    $creditUse.addEventListener('change', () => {
        if (!credit) {
            return;
        }
        credit.on = $creditUse.checked;
        credit.error = '';
        updateTotals();
    });
    $creditAmount.addEventListener('input', () => {
        if (!credit) {
            return;
        }
        const max = creditMax();
        const value = ParseDollars($creditAmount.value);
        if (!value.gt(0)) {
            credit.error = 'Enter an amount of store credit to use.';
        } else if (value.gt(max)) {
            credit.error = `That amount is above ${DollarFormat(max)}.`;
        } else {
            credit.error = '';
            credit.touched = true;
            credit.amount = value;
        }
        updateTotals();
    });
    $creditAmount.addEventListener('blur', () => {
        if (credit && !credit.error) {
            $creditAmount.value = creditApplied().toFixed(2);
        }
    });

    // ─── Split Payment ──────────────────────────────────
    $splitFix.addEventListener('click', () => {
        const fix = splitFix();
        if (!fix) {
            return;
        }
        // Each card goes through its own Remove button, which unloads the card fields and leaves split mode once
        // only Card 1 is left. A single card pays what is left on its own, so its amount is set only in a split.
        for (const block of fix.remove) {
            block.querySelector<HTMLButtonElement>('.remove-card-btn')?.click();
        }
        if (splitMode) {
            fix.input.value = fix.amount.toFixed(2);
        }
        validateSplitAmounts();
    });
    $cardAmt1.addEventListener('input', validateSplitAmounts);
    $cardAmt1.addEventListener('blur', () => autofillNextCardAmount());
    $splitBtn.addEventListener('click', () => {
        if (!splitMode) {
            splitMode = true;
            $fieldsCc.classList.add('split-mode');
            addCardBlock();
            $splitBtn.textContent = '+ Add another card';
        } else {
            addCardBlock();
        }
    });

    // ─── Event Date Toggle ──────────────────────────────
    $eventYes.addEventListener('change', () => {
        $eventDateField.classList.add('open');
        $eventDate.required = true;
        $eventDate.disabled = false;
        revalidateEventDate();
    });
    $eventNo.addEventListener('change', () => {
        $eventDateField.classList.remove('open');
        $eventDate.required = false;
        // Disabling exempts it from validation while keeping the typed date,
        // so a mis-click on "No" doesn't destroy it (the payload is already
        // gated on the "Yes" radio).
        $eventDate.disabled = true;
        revalidateEventDate();
    });
    // A browser-restored date on a collapsed field would otherwise carry a
    // hidden past-date message into the first submit.
    $eventDate.disabled = !$eventYes.checked;

    // TODO: restore when express has promo codes to share
    // $promoApplyBtn.addEventListener('click', () => void applyPromo());
    // $promoInput.addEventListener('keydown', e => {
    //     if (e.key === 'Enter') {
    //         e.preventDefault();
    //         void applyPromo();
    //     }
    // });
    // $promoRemoveBtn.addEventListener('click', removePromo);

    // ─── Place Order ────────────────────────────────────
    // Use the `onclick` property (not addEventListener) so handleDecline can
    // cleanly replace the click target with the retry handler on a decline.
    // With addEventListener, a `$placeOrderBtn.onclick = …` reassignment fires
    // alongside this listener, which would re-run handlePlaceOrder against an
    // already-forgotten cart and 400 with `cartId required`.
    $placeOrderBtn.onclick = () => void handlePlaceOrder();

    // ─── Firefox autofill sync ────────────────────────────
    // Firefox restores select values on refresh but doesn't fire change events.
    // Dispatch change on any select with a non-default restored value so our
    // listeners sync the UI (exemption fields, payment panels, etc.).
    for (const $sel of [$taxExemption, $paymentMethod]) {
        if ($sel.value && !$sel.selectedOptions[0]?.defaultSelected) {
            $sel.dispatchEvent(new Event('change'));
        }
    }
    if (!$billSame.checked) {
        $billSame.dispatchEvent(new Event('change'));
    }
    if ($eventYes.checked) {
        $eventYes.dispatchEvent(new Event('change'));
    }

    // ─── Resume in-flight order (reload after declined charge) ───
    await applyResumableOrder(cartResp.resumableOrder);
    signInInviteReady = true;
    refreshSignInInvite();

    // ─── Cart Polling ───────────────────────────────────
    startCartPolling();

    // ─── IAP Staff Nudge ────────────────────────────────
    // Nudge SMG staff to finish the order in IAP instead of the customer
    // checkout (invisible for everyone else). Runs last so it never blocks
    // checkout setup. Inserts above #checkout-form, at the top of #checkout-content.
    MountStaffNudge({ anchor: $checkoutForm, cartID: smgCarts.GetCartID() || '' });

    // ════════════════════════════════════════════════════
    // Helper Functions
    // ════════════════════════════════════════════════════

    function populateYearSelect(select: HTMLSelectElement) {
        const year = new Date().getFullYear();
        for (let i = 0; i < 12; i++) {
            const opt = document.createElement('option');
            opt.value = String(year + i);
            opt.textContent = String(year + i);
            select.appendChild(opt);
        }
    }

    async function renderSummaryItems(cartItemsInit: models.ICartItem[]) {
        const cartItems = models.CartItem.fromArray(cartItemsInit);
        const f = document.createDocumentFragment();

        for (const cartItem of cartItems) {
            const el = template.Clone('#summary-item-template').firstElementChild as HTMLElement;
            const quote = cartItem.quote;
            const quoteLineItems = quote?.quoteLineItems ?? [];
            const proofs = quote?.quoteProofs || [];

            // One row per top-level line item, matching the cart and quote breakdown.
            const $products = el.querySelector('.summary-item-products') as HTMLElement;
            if (quoteLineItems.length) {
                for (const lineItem of quoteLineItems) {
                    const row = template.Clone('#summary-product-template').firstElementChild as HTMLElement;
                    const $product = row.querySelector('.summary-item-product') as HTMLElement;
                    const $qty = row.querySelector('.summary-item-qty') as HTMLElement;

                    const productName = (await LineItemMap()).get(lineItem.lineItemID || '')?.networkNamePlural() || lineItem.lineItem || '';
                    const parts = [
                        productName,
                        (await TypeMap()).get(lineItem.lineItemTypeID || '')?.networkName() || lineItem.type || '',
                        (await SizeMap()).get(lineItem.lineItemTypeSizeID || '')?.networkName() || lineItem.size || '',
                    ].filter(Boolean);
                    $product.textContent = parts.join(' \u2013 ');

                    const qty = lineItem.quantity || 0;
                    $qty.textContent = `\u00b7 ${qty.toLocaleString()} ${productName || 'pcs'}`;

                    $products.appendChild(row);
                }
            } else {
                const row = template.Clone('#summary-product-template').firstElementChild as HTMLElement;
                const $qty = row.querySelector('.summary-item-qty') as HTMLElement;
                $qty.textContent = `\u00b7 ${(cartItem.quantity || 0).toLocaleString()} pcs`;
                $products.appendChild(row);
            }

            // Meta: serial
            const serial = quote?.serial ?? '';
            const $meta = el.querySelector('.summary-item-meta') as HTMLElement;
            $meta.innerHTML = `<code style="font-size:10px">${serial}</code>`;
            if (quote && cartItem.order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'small text-muted ml-2';
                $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                $meta.appendChild($reorderOf);
            }

            // Price
            const $price = el.querySelector('.summary-item-price') as HTMLElement;
            $price.textContent = DollarFormat(cartItem.subtotal ?? new Big(0));

            // Proof thumbnail with fancybox lightbox
            const $proof = el.querySelector('.summary-item-proof') as HTMLElement;
            if (proofs.length) {
                const firstProof = proofs[0]?.proof;
                if (firstProof) {
                    const urls = firstProof.getURLs();
                    const fancyboxGroup = `checkout-${cartItem.id ?? ''}`;
                    const link = document.createElement('a');
                    link.href = urls.main;
                    link.setAttribute('data-fancybox', fancyboxGroup);
                    const img = document.createElement('img');
                    img.src = urls.thumbnail || urls.main;
                    img.alt = 'Proof';
                    img.className = 'checkout-proof-thumb';
                    img.style.cursor = 'pointer';
                    link.appendChild(img);
                    $proof.replaceWith(link);

                    // Add remaining proofs as hidden fancybox items
                    for (let i = 1; i < proofs.length; i++) {
                        const p = proofs[i]?.proof;
                        if (!p) {
                            continue;
                        }
                        const hidden = document.createElement('a');
                        hidden.href = p.getURLs().main;
                        hidden.setAttribute('data-fancybox', fancyboxGroup);
                        hidden.style.display = 'none';
                        link.after(hidden);
                    }
                }
            }

            f.appendChild(el);
        }

        $summaryItems.appendChild(f);
    }

    // Lists each live project deposit (invoice #, date, amount) behind the
    // Prepayment credit. The deposit set is fixed for the session — only the
    // applied amount moves with shipping/tax/exemption, and that's the
    // Prepayment line in the totals (updateTotals), not this list. Hidden
    // entirely when the cart has no deposits.
    function renderPrepaymentList(depositOrders: models.HyejooCart['pendingDepositOrders']) {
        if (!depositOrders?.length) {
            return;
        }

        const f = document.createDocumentFragment();
        for (const depositOrder of depositOrders) {
            const el = template.Clone('#prepayment-row-template').firstElementChild as HTMLElement;

            const $invoice = el.querySelector('.prepayment-row-invoice') as HTMLElement;
            // Normalize backfills the invoice number on insert; fall back just in case.
            $invoice.textContent = depositOrder.invoiceNumber ? `Invoice #${depositOrder.invoiceNumber}` : 'Deposit';

            const $date = el.querySelector('.prepayment-row-date') as HTMLElement;
            const created = depositOrder.created;
            $date.textContent = created.isValid ? created.toLocaleString(DateTime.DATE_MED) : '';

            const $amount = el.querySelector('.prepayment-row-amount') as HTMLElement;
            $amount.textContent = DollarFormat(depositOrder.paid || 0);

            f.appendChild(el);
        }
        $prepaymentList.replaceChildren(f);
        $prepaymentBlock.classList.remove('d-none');
    }

    // The deposit credit actually applied to this order. The server already
    // caps currentPrepayment at its taxed pre-total (and currentTotal nets it),
    // so the non-exempt value passes through untouched. Exempt mode drops tax,
    // shrinking the cap, so re-clamp against the local pre-prepayment total
    // (subtotal + discount + shipping) to keep the line and the total in
    // agreement.
    function effectivePrepayment(): Big {
        if (!exempt) {
            return currentPrepayment;
        }
        let cap = currentSubtotal.add(currentDiscount).add(currentAvailableDiscount).add(currentShipping);
        if (cap.lt(0)) {
            cap = Big(0);
        }
        return currentPrepayment.gt(cap) ? cap : currentPrepayment;
    }

    // The exempt total is the summary minus tax: the same components the
    // server's total has, with the deposit credit re-capped against them.
    function exemptTotal(): Big {
        const total = currentSubtotal.add(currentDiscount).add(currentAvailableDiscount).add(currentShipping).minus(effectivePrepayment());
        return total.lt(0) ? Big(0) : total;
    }

    function updateTotals() {
        $summarySubtotal.textContent = DollarFormat(currentSubtotal);
        $orderTotal.textContent = DollarFormat(currentTotal);

        if (!currentDiscount.eq(0)) {
            $discountRow.style.display = '';
            $discountRow.style.removeProperty('display');
            $discountAmount.textContent = `\u2212${DollarFormat(currentDiscount.abs())}`;
        } else {
            $discountRow.style.setProperty('display', 'none', 'important');
        }

        if (!currentAvailableDiscount.eq(0)) {
            $availableDiscountRow.style.display = '';
            $availableDiscountRow.style.removeProperty('display');
            // The sign is the recorded line's, not the row's.
            if (currentAvailableDiscount.lt(0)) {
                $availableDiscountAmount.textContent = `\u2212${DollarFormat(currentAvailableDiscount.abs())}`;
            } else {
                $availableDiscountAmount.textContent = DollarFormat(currentAvailableDiscount);
            }
        } else {
            $availableDiscountRow.style.setProperty('display', 'none', 'important');
        }

        const prepayment = effectivePrepayment();
        if (prepayment.gt(0)) {
            $prepaymentRow.style.removeProperty('display');
            $prepaymentValue.textContent = `\u2212${DollarFormat(prepayment)}`;
        } else {
            $prepaymentRow.style.setProperty('display', 'none', 'important');
        }

        updatePaymentSectionVisibility();
        paintCredit();
        paintSummaryPayments();
        validateSplitAmounts();
        updateGSASmartPayWarning();
        refreshSignInInvite();
    }

    // ─── Sign-in invitation ─────────────────────────────
    // Hidden once the checkout pays an existing order: a resume, or an order this page created, has settled its spend.
    function refreshSignInInvite() {
        // The confirmation says the credit shows in the order totals, so it shows only while some of it does.
        const creditShown = creditApplied().gt(0) || !currentAvailableDiscount.eq(0);
        let state = '';
        if (signInInviteReady && orderID === '') {
            if (!GetLoginToken()) {
                state = customerHasCredit ? 'credit' : 'guest';
            } else if (justSignedIn && creditShown && !confirmationDismissed) {
                state = 'confirmation';
            }
        }
        $signInInvite.classList.toggle('d-none', state === '');
        for (const $el of $signInInvite.querySelectorAll<HTMLElement>('[data-sign-in-state]')) {
            $el.classList.toggle('d-none', $el.dataset.signInState !== state);
        }
        // The marks stay for the rest of the visit that came back from signing in, after the confirmation closes,
        // the store credit's only while it is on the order.
        $creditMark.classList.toggle('d-none', !(justSignedIn && creditApplied().gt(0)));
        $availableDiscountMark.classList.toggle('d-none', !justSignedIn);
    }

    // ─── Store credit ───────────────────────────────────
    // What the order still owes on this page: the resumed order's balance, or the total, less what this page paid.
    function dueNow(): Big {
        let due = resumedOrderBalance ?? currentTotal;
        for (const payment of pagePaid.values()) {
            due = due.minus(payment.amount);
        }
        return due.gt(0) ? due : Big(0);
    }

    function creditMax(): Big {
        if (!credit?.balance.gt(0)) {
            return Big(0);
        }
        const due = dueNow();
        return credit.balance.lt(due) ? credit.balance : due;
    }

    // The store credit going toward the order and not taken yet. An amount the customer is still fixing applies nothing.
    function creditApplied(): Big {
        if (!credit?.on || credit.error) {
            return Big(0);
        }
        const max = creditMax();
        if (!credit.touched || credit.amount.gt(max)) {
            return max;
        }
        return credit.amount;
    }

    // What the payment method carries: what is owed, less the credit going toward it.
    function coverAmount(): Big {
        const cover = dueNow().minus(creditApplied());
        return cover.gt(0) ? cover : Big(0);
    }

    function creditCovers(): boolean {
        return creditApplied().gt(0) && !coverAmount().gt(0);
    }

    // A charge whose outcome is unknown retries under the same key and gets the first attempt's result, so once any
    // payment was tried the credit amount stays as it was.
    function creditLocked(): boolean {
        return chargeSequenceActive || paymentTried();
    }

    function paymentTried(): boolean {
        return pagePaid.size > 0 || blockIdemKeys.size > 0 || manualIdemKeys.size > 0 || credit?.idempotencyKey !== undefined;
    }

    function paintCredit() {
        const show = credit !== undefined && credit.balance.gt(0) && !currentTotal.lte(0);
        $credit.classList.toggle('d-none', !show);
        const applied = creditApplied();
        const covers = show && creditCovers();
        if (show && credit) {
            const locked = creditLocked();
            const max = creditMax();
            const kept = credit.balance.minus(applied);
            $creditBalance.textContent = `Your account has ${DollarFormat(credit.balance)} in store credit.`;
            $creditChanged.textContent = `Your store credit balance is now ${DollarFormat(credit.balance)}. Nothing more was charged. Please adjust the amounts and try again.`;
            $creditChanged.classList.toggle('d-none', !credit.changed);
            $creditNotTaken.classList.toggle('d-none', !(credit.status === 'not-taken' && applied.gt(0)));
            $creditUse.checked = credit.on;
            $creditUse.disabled = locked;
            $creditAmountGroup.classList.toggle('d-none', !credit.on);
            $creditAmount.disabled = locked;
            if (document.activeElement !== $creditAmount && !credit.error) {
                $creditAmount.value = applied.gt(0) ? applied.toFixed(2) : '';
            }
            $creditAmount.classList.toggle('is-invalid', Boolean(credit.error));
            $creditAmount.closest('.input-group')?.classList.toggle('is-invalid', Boolean(credit.error));
            $creditAmount.setCustomValidity(credit.error);
            $creditError.textContent = credit.error;
            $creditMax.textContent = `The most you can use on this order is ${DollarFormat(max)}.`;
            $creditMax.classList.toggle('d-none', Boolean(credit.error));
            $creditApplied.textContent = `${DollarFormat(applied)} of your store credit goes toward this order.`;
            $creditKept.textContent = `${DollarFormat(kept)} stays on your account for a later order.`;
            $creditKept.classList.toggle('d-none', !kept.gt(0));
            $creditOutcome.classList.toggle('d-none', !applied.gt(0));
            $creditCovers.classList.toggle('d-none', !covers);
        } else {
            $creditAmount.setCustomValidity('');
        }

        const cover = coverAmount();
        const partial = show && applied.gt(0) && !covers;
        $creditRest.textContent = `Choose how to pay the remaining ${DollarFormat(cover)}.`;
        $creditRest.classList.toggle('d-none', !partial);

        // A check, wire or purchase order carries only what is left once anything else pays part of the order.
        const methodShowsAmount = partial || pagePaid.size > 0 || resumedPaid.length > 0;
        $paymentCard.querySelectorAll<HTMLElement>('.method-amount').forEach($row => {
            $row.classList.toggle('d-none', !methodShowsAmount);
            const $value = $row.querySelector('.method-amount-value');
            if ($value) {
                $value.textContent = DollarFormat(cover);
            }
        });
        $poTotalRule.textContent = methodShowsAmount ? `The purchase order total must match ${DollarFormat(cover)}.` : poTotalRuleDefault;

        // Credit that pays for the whole order takes the payment method's place, and its hidden fields stop counting.
        $paymentMethods.classList.toggle('d-none', covers);
        if (covers || methodsHiddenByCredit) {
            $paymentMethods.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>(
                'input, select, textarea, button',
            ).forEach(el => {
                el.disabled = covers;
            });
        }
        methodsHiddenByCredit = covers;
    }

    // The summary under Total: what is already paid, the store credit going toward the order, and what is left to pay.
    function paintSummaryPayments() {
        const applied = creditApplied();
        const paid = [...resumedPaid, ...pagePaid.values()];
        const show = !currentTotal.lte(0) && (applied.gt(0) || paid.length > 0);
        $summaryPayments.classList.toggle('d-none', !show);
        if (!show) {
            return;
        }

        const f = document.createDocumentFragment();
        for (const payment of paid) {
            const $row = template.Clone('#summary-paid-row-template').firstElementChild as HTMLElement;
            ($row.querySelector('.summary-paid-label') as HTMLElement).textContent = payment.label;
            ($row.querySelector('.summary-paid-amount') as HTMLElement).textContent = `−${DollarFormat(payment.amount)}`;
            f.appendChild($row);
        }
        $summaryPaidRows.replaceChildren(f);

        $summaryCreditRow.classList.toggle('d-none', !applied.gt(0));
        $summaryCreditAmount.textContent = `−${DollarFormat(applied)}`;
        const status = credit?.status ?? 'editing';
        $summaryCreditBadge.classList.toggle('d-none', status === 'editing');
        $summaryCreditBadge.classList.toggle('badge-secondary', status === 'waiting');
        $summaryCreditBadge.classList.toggle('badge-warning', status === 'not-taken');
        $summaryCreditBadge.textContent = status === 'not-taken' ? 'Not Deducted Yet' : 'Waiting';

        // Before Place order the credit is spoken for; once a payment was tried it is still to be taken, so it is owed.
        $summaryLeftAmount.textContent = DollarFormat(status === 'editing' ? coverAmount() : dueNow());
    }

    function setCreditStatus(status: creditStatus) {
        if (credit && creditApplied().gt(0)) {
            credit.status = status;
            paintCredit();
            paintSummaryPayments();
        }
    }

    // takeCredit takes the store credit going toward the order. A balance that no longer covers it is an answered
    // refusal: nothing more is charged, and the page reloads onto the unfinished order with the current balance and
    // the changed note. Any other failure keeps the idempotency key, so the retry replays the first attempt.
    async function takeCredit(user: models.User): Promise<void> {
        const amount = creditApplied();
        if (!credit || !amount.gt(0)) {
            return;
        }
        $placeOrderBtn.innerHTML = '<span class="spinner-border spinner-border-sm mr-2"></span>Applying Store Credit…';
        credit.idempotencyKey ??= crypto.randomUUID();
        try {
            credit.paymentOption ??= await paymentoptions.BalancePaymentOption();
            await CreateOrderPayments({
                user: chargeUser(user),
                orderIDs: [orderID],
                paymentOptionID: credit.paymentOption.key || credit.paymentOption.id,
                amount: Number(amount.toFixed(2)),
                idempotencyKey: credit.idempotencyKey,
            });
        } catch (err) {
            if (CreditRefused(err)) {
                window.sessionStorage.setItem(CREDIT_CHANGED_KEY, '1');
                ClearCache();
                window.location.reload();
                await new Promise(() => undefined);
            }
            setCreditStatus('not-taken');
            throw err;
        }
        pagePaid.set('credit', { label: 'Store Credit', amount });
        credit.balance = credit.balance.minus(amount);
        credit.on = false;
        credit.idempotencyKey = undefined;
    }

    // The BIN is a hint, not proof (GSA travel cards share the purchase-card
    // prefixes), so this only ever warns — never blocks or auto-exempts (#3777).
    // gsaSmartPayWarned is set when the customer declines the place-order
    // prompt: the inline alert stays behind as context for why the order
    // didn't go through — but only while a card is still the selected
    // method; switching to check/wire/PO takes the card out of play.
    function updateGSASmartPayWarning(): void {
        $gsaSmartPayTaxAmount.textContent = DollarFormat(currentTax);
        $gsaSmartPayWarning.classList.toggle('d-none', !(gsaSmartPayWarned && $paymentMethod.value === 'cc' && currentTax.gt(0)));
    }

    // When a project deposit (or discount) fully covers the order the netted
    // total is $0 and no payment is due — the matching deposit orderpayments
    // transfer onto the new order server-side. Hide the whole payment section
    // and show a covered message so the customer isn't asked to enter a card
    // for a $0 charge (which would block place-order entirely). Disabling the
    // hidden section's controls keeps any required field the customer may have
    // revealed (e.g. a separate billing address) from blocking submission.
    // Reversible: called from updateTotals on every recompute, so it restores
    // the payment section if shipping or a tax change pushes the total back
    // above $0.
    function updatePaymentSectionVisibility() {
        const noPaymentDue = currentTotal.lte(0);
        if (noPaymentDue) {
            if (currentPrepayment.gt(0)) {
                $noPaymentNeeded.textContent = 'Your deposit covers the full order total — no additional payment is required.';
            } else {
                $noPaymentNeeded.textContent = 'Your order is free! No payment is required.';
            }
        }
        $paymentCard.classList.toggle('d-none', noPaymentDue);
        $noPaymentNeeded.classList.toggle('d-none', !noPaymentDue);
        $paymentCard.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>(
            'input, select, textarea, button',
        ).forEach(el => {
            el.disabled = noPaymentDue;
        });
    }

    // The exempt paint is shared by the exemption toggle and by a summary
    // refresh that lands while exempt: setTaxState('loaded') would print a
    // bare $0.00 and drop the "(exempt)" the customer already saw.
    function showExemptTax() {
        $taxValue.style.display = '';
        $taxValue.textContent = '$0.00 (exempt)';
        $taxValue.classList.add('text-muted');
        $taxValue.classList.remove('font-weight-500');
        $taxRateLabel.textContent = '';
        $taxSpinner.style.display = 'none';
    }

    function setTaxState(state: 'loading' | 'loaded' | 'error') {
        if (state === 'loading') {
            $taxSpinner.style.display = '';
            $taxValue.style.display = 'none';
        } else if (state === 'loaded') {
            $taxSpinner.style.display = 'none';
            $taxValue.style.display = '';
            $taxValue.textContent = DollarFormat(currentTax);
            $taxValue.classList.remove('text-muted');
            $taxValue.classList.add('font-weight-500');

            if (currentTaxRate.gt(0)) {
                $taxRateLabel.textContent = `(${currentTaxRate.times(100).toFixed(2)}%)`;
            } else {
                $taxRateLabel.textContent = '';
            }
        } else {
            $taxSpinner.style.display = 'none';
            $taxValue.style.display = '';
            $taxValue.textContent = 'Unable to calculate \u2014 will be confirmed at processing';
            $taxValue.classList.add('text-muted');
            $taxValue.classList.remove('font-weight-500');
            $taxRateLabel.textContent = '';
        }
    }

    // ─── BlockChyp mount helpers ────────────────────────
    function mountCardBlock(blockEl: HTMLElement) {
        const blockId = blockEl.id;
        const existing = cardMounts.get(blockId);
        if (existing && !existing.isUnloaded) {
            return;
        }
        if (existing) {
            // Retired mount (a tokenize timed out, which removes its frames and
            // listener — see ts/blockchyp.ts). Rebuild so the customer has live
            // fields to re-enter the card into.
            cardMounts.delete(blockId);
            cardInputHandlers.delete(blockId);
        }
        const $number = blockEl.querySelector<HTMLElement>('.card-number-input') ??
            (blockEl.id === 'card-block-1' ? document.getElementById('card-number-1') : null);
        const $cvc = blockEl.querySelector<HTMLElement>('.cvc-input') ??
            (blockEl.id === 'card-block-1' ? document.getElementById('cvc-1') : null);
        if (!$number || !$cvc || !$number.id || !$cvc.id) {
            throw new Error(`mountCardBlock: missing iframe mount elements for ${blockId}`);
        }

        const mount = new blockchyp.Mount({
            webPaymentsToken: resolveMerchantToken(),
            numberElID: $number.id,
            cvvElID: $cvc.id,
            numberWrapper: $number,
            cvvWrapper: $cvc,
            cardFieldsContainer: $fieldsCc,
            alternatePayments: blockchyp.OtherPaymentMethodNames($paymentMethod),
            fieldStyle: expressCardFieldStyle,
            // PAN/CVC live inside the BlockChyp iframe and don't bubble
            // input events to our page. BlockChyp's validate fires on blur
            // only while cardType fires while typing, so the decline-retry
            // "user touched the iframe" signal comes from cardType +
            // fieldEvent rather than Spreedly's per-keystroke validation.
            //
            // Block ids are immutable for the life of a mount (see
            // addCardBlock), so capturing `blockId` once here is safe.
            onValidation: () => cardInputHandlers.get(blockId)?.(),
            onFieldEvent: () => cardInputHandlers.get(blockId)?.(),
        });
        cardMounts.set(blockId, mount);
        // Drop the dead mount and the shared merchant promise so the next
        // mountCardBlock rebuilds.
        void mount.ready.catch(() => {
            mount.unload();
            // A remount can replace this block's registry entry before a slow
            // ready rejection lands — only clear the slot if it still holds
            // THIS mount, or the delayed catch would orphan the replacement.
            if (cardMounts.get(blockId) === mount) {
                cardMounts.delete(blockId);
                cardInputHandlers.delete(blockId);
                merchantToken = undefined;
            }
        });
    }

    // Rebuild any card block whose mount is missing or retired (initial
    // mount.ready failure deletes the entry; tokenize timeout unloads and
    // leaves isUnloaded). Without this the block is left with no iframes and
    // the customer cannot retry — every later submit throws
    // getCardFieldsFromBlock: no BlockChyp mount.
    function remountRetiredCardBlocks() {
        for (const block of getCardBlockElements()) {
            const mount = cardMounts.get(block.id);
            if (!mount || mount.isUnloaded) {
                mountCardBlock(block);
            }
        }
    }

    function unloadCardBlock(blockEl: HTMLElement) {
        const blockId = blockEl.id;
        cardMounts.get(blockId)?.unload();
        cardMounts.delete(blockId);
        cardInputHandlers.delete(blockId);
    }

    function unloadAllCardBlocks() {
        for (const mount of cardMounts.values()) {
            mount.unload();
        }
        cardMounts.clear();
        cardInputHandlers.clear();
    }

    // BlockChyp iframes are cross-origin and can't be `disabled` from our
    // side. Without this, in split-payment a user could edit card N+1's
    // PAN/CVC while card N is charging — chargeCardsSequentially would
    // then charge card N+1 with the pre-flight cached token (stale)
    // instead of what the iframe now shows. pointer-events:none on the
    // wrapper blocks the iframe from receiving focus/clicks.
    function freezeCardIframes() {
        for (const block of getCardBlockElements()) {
            block.querySelectorAll<HTMLElement>('.card-iframe-mount').forEach(el => {
                el.style.pointerEvents = 'none';
                el.style.opacity = '0.6';
                el.querySelectorAll('iframe').forEach(iframe => iframe.setAttribute('tabindex', '-1'));
            });
        }
    }

    // Pass `blocks` to thaw only those (e.g. declined + unreached so a
    // successfully-charged card stays locked). Defaults to thawing every
    // block — correct for pre-charge failure paths where no card has been
    // taken money on yet.
    function thawCardIframes(blocks?: HTMLElement[]) {
        const target = blocks ?? getCardBlockElements();
        for (const block of target) {
            block.querySelectorAll<HTMLElement>('.card-iframe-mount').forEach(el => {
                el.style.pointerEvents = '';
                el.style.opacity = '';
                el.querySelectorAll('iframe').forEach(iframe => iframe.removeAttribute('tabindex'));
            });
        }
    }

    // ─── Split Payment Helpers ──────────────────────────
    function addCardBlock() {
        nextCardBlockId++;
        const id = nextCardBlockId;
        const el = template.Clone('#card-block-template').firstElementChild as HTMLElement;
        el.id = `card-block-${id}`;

        // Populate year select
        const $yearSelect = el.querySelector('.exp-year-select') as HTMLSelectElement;
        populateYearSelect($yearSelect);

        // BlockChyp iframe mount divs use the same monotonic id, so once
        // a Mount captures these element ids at construction it never
        // sees them migrate or collide with a later addCardBlock — even
        // if a middle block is removed before this Mount finishes
        // initializing.
        const $numberMount = el.querySelector('.card-number-input') as HTMLElement;
        const $cvcMount = el.querySelector('.cvc-input') as HTMLElement;
        $numberMount.id = `card-number-${id}`;
        $cvcMount.id = `cvc-${id}`;

        // Amount validation
        const $amtInput = el.querySelector('.card-amt-input') as HTMLInputElement;
        $amtInput.addEventListener('input', validateSplitAmounts);
        $amtInput.addEventListener('blur', () => autofillNextCardAmount());

        // Remove button
        const $removeBtn = el.querySelector('.remove-card-btn') as HTMLButtonElement;
        $removeBtn.addEventListener('click', () => {
            unloadCardBlock(el);
            el.remove();
            relabelCardBlocks();
            validateSplitAmounts();

            // If only 1 card left, exit split mode
            if ($additionalCards.children.length === 0) {
                resetSplitPayment();
            }
        });

        $additionalCards.appendChild(el);
        relabelCardBlocks();
        mountCardBlock(el);
    }

    // Refresh "Card N" labels based on current DOM order. Element ids
    // (block.id, card-number-N, cvc-N) are immutable for the life of a
    // mount and intentionally NOT touched here — see addCardBlock.
    function relabelCardBlocks() {
        const blocks = $additionalCards.querySelectorAll<HTMLElement>('.payment-card-block');
        blocks.forEach((block, i) => {
            const label = block.querySelector('.card-block-label') as HTMLElement;
            if (label) {
                label.textContent = `Card ${i + 2}`;
            }
        });
    }

    function resetSplitPayment() {
        splitMode = false;
        $fieldsCc.classList.remove('split-mode');
        // Unload BlockChyp mounts owned by additional blocks before
        // wiping the DOM so listeners aren't left bound to detached nodes.
        for (const block of $additionalCards.querySelectorAll<HTMLElement>('.payment-card-block')) {
            unloadCardBlock(block);
        }
        $additionalCards.innerHTML = '';
        // Don't reset nextCardBlockId — keeping it monotonic across
        // split toggles preserves the invariant that no BlockChyp Mount
        // ever sees its captured element id reused by a later block.
        $cardAmt1.value = '';
        $splitTally.classList.add('d-none');
        $splitBtn.textContent = '+ Split payment across multiple cards';
    }

    // What the split's cards add up to: the order less the store credit going toward it. Cards this page already
    // charged are part of the split, so their amounts count.
    function splitTarget(): Big {
        let target = (resumedOrderBalance ?? currentTotal).minus(creditApplied());
        const taken = pagePaid.get('credit');
        if (taken) {
            target = target.minus(taken.amount);
        }
        return target.gt(0) ? target : Big(0);
    }

    function splitSum(): Big {
        let sum = Big(0);
        for (const input of getAllCardAmountInputs()) {
            const val = parseFloat(input.value);
            if (!isNaN(val) && val > 0) {
                sum = sum.add(Big(val));
            }
        }
        return sum;
    }

    // The one-tap fix sets one card that can still change to what makes the split add up: the last such card that
    // would still carry a positive amount. When no card can, because every other card already carries too much, the
    // fix puts what is left on the first card that can still change and removes the other uncharged cards.
    function splitFix(): splitFixPlan | undefined {
        const inputs = getAllCardAmountInputs();
        for (let i = inputs.length - 1; i >= 0; i--) {
            const input = inputs[i];
            if (input.disabled) {
                continue;
            }
            const val = parseFloat(input.value);
            const others = splitSum().minus(!isNaN(val) && val > 0 ? Big(val) : Big(0));
            const amount = splitTarget().minus(others);
            if (amount.gt(0)) {
                return { input, label: `Card ${i + 1}`, amount, remove: [] };
            }
        }

        const keep = inputs.findIndex(input => !input.disabled);
        if (keep < 0) {
            return undefined;
        }
        let charged = Big(0);
        for (const input of inputs) {
            const val = parseFloat(input.value);
            if (input.disabled && !isNaN(val) && val > 0) {
                charged = charged.add(Big(val));
            }
        }
        const amount = splitTarget().minus(charged);
        if (!amount.gt(0)) {
            return undefined;
        }
        const remove = inputs.filter((input, i) => i !== keep && !input.disabled).
            map(input => input.closest<HTMLElement>('#additional-card-blocks .payment-card-block')).
            filter((block): block is HTMLElement => block !== null);
        return { input: inputs[keep], label: `Card ${keep + 1}`, amount, remove };
    }

    function validateSplitAmounts() {
        if (!splitMode) {
            $splitTally.classList.add('d-none');
            return true;
        }

        const sum = splitSum();
        const target = splitTarget();
        const valid = sum.eq(target);
        if (valid) {
            $splitTallyText.textContent = `Your card amounts add up to ${DollarFormat(sum)}, the amount left to pay.`;
        } else {
            const more = sum.gt(target);
            const diff = DollarFormat(more ? sum.minus(target) : target.minus(sum));
            $splitTallyText.textContent = `Your card amounts add up to ${DollarFormat(sum)}, which is ${diff} ${more ? 'more' : 'less'} than the ${DollarFormat(target)} left to pay.`;
        }
        $splitTally.classList.remove('d-none');
        $splitTally.classList.toggle('text-success', valid);
        $splitTally.classList.toggle('text-danger', !valid);
        const fix = valid ? undefined : splitFix();
        $splitFix.classList.toggle('d-none', !fix);
        if (fix && fix.remove.length > 0) {
            $splitFix.textContent = `Set ${fix.label} to ${DollarFormat(fix.amount)} and remove other cards from this payment`;
        } else if (fix) {
            $splitFix.textContent = `Set ${fix.label} to ${DollarFormat(fix.amount)}`;
        }
        return valid;
    }

    function autofillNextCardAmount() {
        if (!splitMode) {
            return;
        }

        const amtInputs = getAllCardAmountInputs();
        if (amtInputs.length < 2) {
            return;
        }

        // If only the first card has an amount, auto-fill the last card
        let filledCount = 0;
        let totalFilled = Big(0);
        let lastEmpty: HTMLInputElement | undefined;

        for (const input of amtInputs) {
            const val = parseFloat(input.value);
            if (!isNaN(val) && val > 0) {
                filledCount++;
                totalFilled = totalFilled.add(Big(val));
            } else {
                lastEmpty = input;
            }
        }

        if (filledCount === amtInputs.length - 1 && lastEmpty) {
            const remaining = splitTarget().minus(totalFilled);
            if (remaining.gt(0)) {
                lastEmpty.value = remaining.toFixed(2);
                validateSplitAmounts();
            }
        }
    }

    function getAllCardAmountInputs(): HTMLInputElement[] {
        const inputs: HTMLInputElement[] = [$cardAmt1];
        $additionalCards.querySelectorAll<HTMLInputElement>('.card-amt-input').forEach(input => {
            inputs.push(input);
        });
        return inputs;
    }

    // TODO: restore when express has promo codes to share
    // async function applyPromo() { ... }
    // function removePromo() { ... }
    // function getShippingAddress() { ... }

    // ─── Cart Polling ───────────────────────────────────
    function startCartPolling() {
        // The poll exists to notice the cart changing under the page (another tab,
        // an expired item), not to mirror the summary: it compares live, untaxed
        // totals against a baseline fetched the same way, never against the
        // taxed/resumed figure the summary shows.
        let initialTotal: string | undefined;
        void smgCarts.GetCart('totals').then(resp => {
            initialTotal = (resp?.totals.total ?? Big(0)).toString();
        }).catch(() => undefined);

        cartPollInterval = setInterval(async () => {
            if (chargeSequenceActive) {
                return;
            }

            try {
                const resp = await smgCarts.GetCart('cartItems,totals');
                if (!resp?.cartItems.length) {
                    window.location.reload();
                    return;
                }
                const newTotal = resp.totals.total.toString();
                if (initialTotal === undefined) {
                    // One failed initial fetch must not disable change detection forever.
                    initialTotal = newTotal;
                    return;
                }
                if (newTotal !== initialTotal) {
                    window.location.reload();
                }
            } catch {
                // ignore polling errors
            }
        }, 30000);
    }

    function stopCartPolling() {
        if (cartPollInterval) {
            clearInterval(cartPollInterval);
            cartPollInterval = undefined;
        }
    }

    // ─── Validation ─────────────────────────────────────
    // Set custom validity on fields that need runtime checks beyond HTML required/pattern,
    // then let ReportValidity handle .is-invalid, feedback text, scroll, and auto-clear.
    function validateForm(): boolean {
        // CC card fields aren't in the HTML with required/pattern because they're
        // only required when CC is selected. Set validity dynamically.
        const method = $paymentMethod.value;
        let uploadMissing = false;

        // A deposit (or discount) fully covering the order means no payment is
        // due — the payment section is hidden, so skip its field validation.
        // Store credit that covers the order hides the method the same way.
        const noPaymentDue = currentTotal.lte(0) || creditCovers();

        if (!noPaymentDue && method === 'cc') {
            setCardBlockValidity(document.getElementById('card-block-1') as HTMLElement);
            if (splitMode) {
                $additionalCards.querySelectorAll<HTMLElement>('.payment-card-block').forEach(block => {
                    setCardBlockValidity(block);
                });
                validateSplitAmounts();
            }
        }
        if (!noPaymentDue && method === 'po') {
            $poNumber.setCustomValidity($poNumber.value.trim() ? '' : 'This field is required.');
            if (!poUploader.uploads().length) {
                $poUploadFeedback.style.display = 'block';
                uploadMissing = true;
            }
        }

        // Upload-based fields: file uploads aren't native form controls,
        // so flag the nearest real input if missing.
        // The exemption and its documents are already on the created order; resume only pays it.
        if (orderID === '') {
            if ($exemptionFields.classList.contains('open') && !exemptUploader.uploads().length) {
                $exemptUploadFeedback.style.display = 'block';
                uploadMissing = true;
            }
        } else {
            $exemptNumber.required = false;
            $exemptState.required = false;
        }

        // Verification checkbox message
        $verify.setCustomValidity($verify.checked ? '' : 'You must verify your order details.');

        // Run ReportValidity first to show all field-level errors, then block if uploads are missing.
        const formValid = ReportValidity($checkoutForm);
        if (uploadMissing) {
            return false;
        }
        return formValid;
    }

    function setCardBlockValidity(block: HTMLElement) {
        // PAN + CVC validity is enforced by the BlockChyp iframe (luhn,
        // length per card brand). Tokenization rejects invalid input
        // and we surface that error in chargeCardsSequentially.

        if (splitMode) {
            const $amt = (block.querySelector('.card-amt-input') ?? block.querySelector('#card-amt-1')) as HTMLInputElement;
            if ($amt) {
                const val = parseFloat($amt.value);
                $amt.setCustomValidity(!isNaN(val) && val > 0 ? '' : 'Enter a valid amount.');
            }
        }
    }

    // ─── User helper ────────────────────────────────────
    function userDetails(): models.User {
        return new models.User({
            ...GetFirstAndLast($shippingName.value.trim()),
            phone: $phone.value.trim(),
            email: $email.value.trim(),
            company: $shippingCompany.value.trim() || undefined,
            countryID: parseInt($shippingCountry.value, 10) || undefined,
            stateID: parseInt($shippingState.value, 10) || undefined,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }

    // OrderPaymentsCreateRequest.user is NewUserDetails (not User — fullName is a
    // method on User and a string field on NewUserDetails).
    function chargeUser(user: models.User): models.NewUserDetails {
        return new models.NewUserDetails({
            firstName: user.firstName,
            lastName: user.lastName,
            company: user.company,
            phone: user.phone,
            email: user.email,
            countryID: user.countryID,
            stateID: user.stateID,
            pageRequestID: user.pageRequestID,
            tz: user.tz,
        });
    }

    // ─── Place Order ────────────────────────────────────
    async function handlePlaceOrder() {
        if (!validateForm()) {
            return;
        }

        // Disable everything
        const fieldset = $checkoutForm.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>(
            'input, select, textarea, button',
        );
        fieldset.forEach(el => {
            el.disabled = true;
        });
        $placeOrderBtn.disabled = true;
        $placeOrderBtn.innerHTML = '<span class="spinner-border spinner-border-sm mr-2"></span>Placing Order\u2026';

        // Stop cart polling
        stopCartPolling();
        chargeSequenceActive = true;
        setCreditStatus('waiting');

        const method = $paymentMethod.value;
        // Deposit (or discount) fully covers the order — nothing to charge. The
        // matching deposit orderpayments transfer onto the new order
        // server-side; we just create the order and finalize.
        const noPaymentDue = currentTotal.lte(0);
        // Store credit pays for the whole order: nothing goes on a card or another method.
        const coveredByCredit = !noPaymentDue && creditCovers();
        const cardCharge = method === 'cc' && !noPaymentDue && !coveredByCredit;

        // Freeze iframe interaction for the entire CC submit window
        // (tokenize → shipping → exemption → /orders → charge). The
        // native fieldset disable above doesn't affect cross-origin
        // BlockChyp iframes; without this freeze, the user could edit
        // PAN/CVC AFTER tokenizeAllCardBlocks caches a token and the
        // cached (stale) token would still be what we charge. Thawed
        // on any failure path via the catch handler below, and on
        // decline/success in chargeCardsSequentially.
        if (cardCharge) {
            freezeCardIframes();
        }

        try {
            const user = userDetails();
            user.pageRequestID = await GetPageRequestID();
            user.phone = await phoneInput.format();

            // 1. Pre-tokenize all card blocks BEFORE any persistent API
            // writes (shipping option, tax exemption, order). Missing/
            // invalid iframe input rejects here, so we don't orphan a
            // shipping option or any other resource on a doomed submit.
            if (cardCharge) {
                await tokenizeAllCardBlocks(getCardBlockElements());
            }

            // Prompt when a GSA SmartPay government card is about to pay
            // sales tax (#3777): tokenizing above is the first time the BIN is
            // visible. Confirming continues this same submission — nothing has
            // been persisted yet, so declining aborts safely and re-clicking
            // Place order re-tokenizes exactly like a validation failure (and
            // re-prompts, since only confirming acknowledges).
            // Acknowledgment is per-BIN, so re-tokenizing a different GSA
            // card prompts again.
            if (cardCharge && currentTax.gt(0)) {
                const unacknowledged = Array.from(cardTokens.values()).
                    map(tokenized => tokenized.firstSix || '').
                    filter(firstSix => IsGSASmartPay(firstSix) && !gsaSmartPayAcknowledgedFirst6s.has(firstSix));
                if (unacknowledged.length) {
                    const gsaSmartPay = await import('smg/gsa-smartpay');
                    const choice = await gsaSmartPay.ConfirmGSASmartPay(DollarFormat(currentTax));
                    if (choice !== 'continue') {
                        gsaSmartPayWarned = true;
                        updateGSASmartPayWarning();
                        reEnableForm(fieldset);
                        thawCardIframes();
                        chargeSequenceActive = false;
                        startCartPolling();
                        if (choice === 'exemption') {
                            $gsaSmartPayAddExemption.click();
                        } else {
                            $gsaSmartPayWarning.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                        return;
                    }
                    unacknowledged.forEach(firstSix => gsaSmartPayAcknowledgedFirst6s.add(firstSix));
                }
            }

            // A retry after a failed charge/checkout-complete must reuse the
            // already-created order — re-running creation would duplicate the
            // order and re-charge cards (#8118).
            if (!orderID) {
                // 2. Create shipping option
                const newShippingOption = await shippingoptions.NewShippingOption(user, new models.ShippingOption({
                    address: {
                        fullName: $shippingName.value.trim(),
                        organization: $shippingCompany.value.trim(),
                        countryID: parseInt($shippingCountry.value, 10),
                        addressLine1: $shippingAddress1.value.trim(),
                        addressLine2: $shippingAddress2.value.trim(),
                        city: $shippingCity.value.trim(),
                        stateID: parseInt($shippingState.value, 10),
                        postalCode: $shippingZip.value.trim(),
                        phoneNumber: { phone: user.phone },
                        emailAddress: { email: $email.value.trim() },
                    },
                }).toJSON());

                // 3. Handle tax exemption
                let userTaxExemptionID: string | undefined;
                if ($taxExemption.value && $taxExemption.value !== 'none') {
                    const exemption = await usertaxexemptions.NewUserTaxExemption({
                        user,
                        userTaxExemptReasonID: getExemptReasonID($taxExemption.value),
                        number: $exemptNumber.value.trim(),
                        countryID: parseInt($shippingCountry.value, 10),
                        stateID: parseInt($exemptState.value, 10) || undefined,
                        uploadIDs: await exemptUploader.uploadIDs(),
                    });
                    userTaxExemptionID = exemption.id;
                }

                // 4. Create order
                const orderReq: Record<string, unknown> = {
                    'user': user,
                    'cartID': smgCarts.GetCartID(),
                    'shippingOptionIDs': [newShippingOption.id],
                    'sms': $permSms.checked,
                    'promotionalApproval': $permMarketing.checked,
                    'notes': document.getElementById('checkout-notes') ? (document.getElementById('checkout-notes') as HTMLTextAreaElement).value.trim() : '',
                };

                if (appliedPromo) {
                    orderReq['promo'] = appliedPromo;
                }
                if (userTaxExemptionID) {
                    orderReq['userTaxExemptionID'] = userTaxExemptionID;
                }
                if ($eventYes.checked && $eventDate.value) {
                    orderReq['eventDate'] = $eventDate.value;
                }

                const orderResp = await axios.post('/orders', orderReq).then(r => models.OrderCreateResponse.fromJSON(r.data));
                orderID = orderResp.orderID;
                refreshSignInInvite();
                orderKey = orderResp.key;
            }

            // Fully covered by the deposit — no payment option, no charge.
            // Finalize and redirect (the deposit orderpayments are transferred
            // onto the new order server-side).
            if (noPaymentDue) {
                await axios.post(`/orders/${orderID}/checkout-complete`);
                await redirectToConfirmation(orderKey);
                return;
            }

            if (coveredByCredit) {
                await takeCredit(user);
                await axios.post(`/orders/${orderID}/checkout-complete`);
                await redirectToConfirmation(orderKey);
                return;
            }

            // Non-CC methods: redirect immediately
            if (method !== 'cc') {
                // These cannot decline, so the credit goes first and the method records what is left.
                await takeCredit(user);
                const manualAmount = pagePaid.has('credit') ? Number(coverAmount().toFixed(2)) : undefined;
                if (method === 'po') {
                    // Create PO payment option
                    const paymentOption = await createPaymentOption(models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO);
                    const poType = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO;
                    let k = manualIdemKeys.get(poType);
                    if (!k) {
                        k = crypto.randomUUID();
                        manualIdemKeys.set(poType, k);
                    }

                    await CreateOrderPayments({
                        user: chargeUser(user),
                        orderIDs: [orderID],
                        paymentOptionID: paymentOption.key || paymentOption.id,
                        poNumber: $poNumber.value.trim(),
                        poUploadIDs: await poUploader.uploadIDs(),
                        amount: manualAmount,
                        idempotencyKey: k,
                    });
                } else if (method === 'check') {
                    const paymentOption = await createPaymentOption(models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK);
                    const checkType = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK;
                    let k = manualIdemKeys.get(checkType);
                    if (!k) {
                        k = crypto.randomUUID();
                        manualIdemKeys.set(checkType, k);
                    }
                    await CreateOrderPayments({
                        user: chargeUser(user),
                        orderIDs: [orderID],
                        paymentOptionID: paymentOption.key || paymentOption.id,
                        amount: manualAmount,
                        idempotencyKey: k,
                    });
                } else if (method === 'wire') {
                    const paymentOption = await createPaymentOption(models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE);
                    const wireType = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE;
                    let k = manualIdemKeys.get(wireType);
                    if (!k) {
                        k = crypto.randomUUID();
                        manualIdemKeys.set(wireType, k);
                    }
                    await CreateOrderPayments({
                        user: chargeUser(user),
                        orderIDs: [orderID],
                        paymentOptionID: paymentOption.key || paymentOption.id,
                        amount: manualAmount,
                        idempotencyKey: k,
                    });
                }

                await axios.post(`/orders/${orderID}/checkout-complete`);
                await redirectToConfirmation(orderKey);
                return;
            }

            // CC: charge cards sequentially
            const cardBlocks = getCardBlockElements();

            // normalizeNewOrderRealmToSalesTeam (#7537) can re-home a *new*
            // order onto the sales team's primary realm when that team does not
            // serve the cart's realm. The BlockChyp tokenizing key is
            // per-merchant, so tokens minted under the cart's merchant cannot
            // be exchanged under the order's merchant. Mounts were built from
            // the cart-resolved token before /orders; after create, compare
            // with the order's merchant and fail early + remount rather than a
            // cryptic exchange error (same fail-loudly posture as the prepay
            // residual in go/lambda/customer-api/orders.go — do not re-home
            // money server-side). PaymentMerchant is memoized; NewPaymentOption
            // makes the identical orderIDs call moments later, so no extra RTT.
            const orderMerchant = await paymentoptions.PaymentMerchant({ orderIDs: [orderID] });
            // Read the CACHED promise, never resolveMerchantToken() — orderID is
            // set by now, so that helper would resolve by orderIDs and compare
            // the order's merchant against itself, which can never mismatch.
            const mountToken = await merchantToken?.catch(() => undefined);
            // Fail CLOSED on missing provenance: a pending token always came
            // from a mount, and every mount caches merchantToken — so an
            // undefined mountToken here means the cache was cleared after
            // tokenizing (lost provenance), not "never mounted". Treat it
            // exactly like a mismatch rather than skipping the check.
            if (orderMerchant.webPaymentsToken && orderMerchant.webPaymentsToken !== mountToken) {
                cardTokens.clear();
                merchantToken = undefined;
                for (const block of cardBlocks) {
                    unloadCardBlock(block);
                    mountCardBlock(block);
                }
                thawCardIframes(cardBlocks);
                // Order exists and is valid — leave it and use the decline/retry
                // path to charge it once the customer re-enters under the order
                // merchant. Do not throw (that re-enables Place order and would
                // try to create another order against a forgotten cart).
                handleDecline(cardBlocks[0], {
                    alertHtml: `<strong>Your order was placed, but the card needs to be re-entered to complete payment.</strong>
                        Update the card details above and retry. Your order has been saved &mdash;
                        previously charged amounts will not be re-charged.`,
                });
                return;
            }

            await chargeCardsSequentially(cardBlocks, user);
        } catch (err) {
            // Field-scoped empty/invalid card errors paint is-invalid on the
            // mount wrapper — no generic modal (inline feedback is the message).
            if (!(err instanceof blockchyp.TokenizeFieldError)) {
                ShowError(err);
            }
            reEnableForm(fieldset);
            // Thaw iframes so the user can fix whatever caused the
            // failure (bad card, missing field, etc.) and retry. No-op
            // for non-CC methods.
            thawCardIframes();
            // A tokenize timeout retires its mount (frames removed), so give
            // any retired block fresh fields to retry into.
            remountRetiredCardBlocks();
            chargeSequenceActive = false;
            startCartPolling();
        }
    }

    // Pre-tokenize every card block before /orders is created. If any
    // iframe has empty/invalid PAN or CVC, BlockChyp's tokenize promise
    // rejects so we surface it as a validation failure here instead of
    // letting handlePlaceOrder create an order and forget the cart, then
    // have chargeCardsSequentially turn the bad data into a "decline".
    async function tokenizeAllCardBlocks(blocks: HTMLElement[]): Promise<void> {
        cardTokens.clear();
        for (const block of blocks) {
            clearCardFieldInvalid(block);
        }
        for (const block of blocks) {
            const fields = getCardFieldsFromBlock(block);
            try {
                const tokenized = await fields.mount.tokenize(buildTokenizeOpts(fields));
                cardTokens.set(block.id, tokenized);
            } catch (err) {
                if (err instanceof blockchyp.TokenizeFieldError) {
                    markCardFieldInvalid(block, err.field);
                }
                throw err;
            }
        }
    }

    function cardMountWrappers(block: HTMLElement): { $number: HTMLElement | null; $cvc: HTMLElement | null } {
        const $number = block.querySelector<HTMLElement>('.card-number-input') ??
            (block.id === 'card-block-1' ? document.getElementById('card-number-1') : null);
        const $cvc = block.querySelector<HTMLElement>('.cvc-input') ??
            (block.id === 'card-block-1' ? document.getElementById('cvc-1') : null);
        return { $number, $cvc };
    }

    function clearCardFieldInvalid(block: HTMLElement): void {
        const { $number, $cvc } = cardMountWrappers(block);
        $number?.classList.remove('is-invalid');
        $cvc?.classList.remove('is-invalid');
    }

    function markCardFieldInvalid(block: HTMLElement, field: 'number' | 'cvv'): void {
        const { $number, $cvc } = cardMountWrappers(block);
        const $wrapper = field === 'number' ? $number : $cvc;
        if ($wrapper) {
            $wrapper.classList.add('is-invalid');
            $wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }

    function buildTokenizeOpts(fields: ReturnType<typeof getCardFieldsFromBlock>): blockchyp.TokenizeOpts {
        const useBilling = !$billSame.checked;
        const billingCountrySel = useBilling ? $billingCountry : $shippingCountry;
        const billingStateSel = useBilling ? $billingState : $shippingState;
        return {
            fullName: useBilling ? $billingName.value.trim() : $shippingName.value.trim(),
            month: parseInt(fields.expMonth.value, 10),
            year: parseInt(fields.expYear.value, 10),
            addressLine1: useBilling ? $billingAddress1.value.trim() : $shippingAddress1.value.trim(),
            addressLine2: useBilling ? $billingAddress2.value.trim() : $shippingAddress2.value.trim(),
            city: useBilling ? $billingCity.value.trim() : $shippingCity.value.trim(),
            stateCode: billingStateSel.options[billingStateSel.selectedIndex]?.getAttribute('data-state-code') || undefined,
            zip: useBilling ? $billingZip.value.trim() : $shippingZip.value.trim(),
            countryCode3: billingCountrySel.options[billingCountrySel.selectedIndex]?.getAttribute('data-country-code-3') || undefined,
            company: $shippingCompany.value.trim() || undefined,
        };
    }

    async function createPaymentOption(typeID: models.PaymentOptionTypeID): Promise<models.PaymentOption> {
        const useBilling = !$billSame.checked;
        const cID = parseInt(useBilling ? $billingCountry.value : $shippingCountry.value, 10);
        const sID = parseInt(useBilling ? $billingState.value : $shippingState.value, 10);
        const po = new models.PaymentOption({
            fullName: useBilling ? $billingName.value.trim() : $shippingName.value.trim(),
            company: useBilling ? '' : $shippingCompany.value.trim(),
            countryID: cID,
            addressLine1: useBilling ? $billingAddress1.value.trim() : $shippingAddress1.value.trim(),
            addressLine2: useBilling ? $billingAddress2.value.trim() : $shippingAddress2.value.trim(),
            city: useBilling ? $billingCity.value.trim() : $shippingCity.value.trim(),
            stateID: sID,
            zip: useBilling ? $billingZip.value.trim() : $shippingZip.value.trim(),
            phone: await phoneInput.format(),
            email: $email.value.trim(),
            paymentOptionTypeID: typeID,
        });

        await countries.UpdateCountryAndState(po);

        const user = userDetails();
        user.phone = await phoneInput.format();

        return paymentoptions.NewPaymentOption({
            user,
            paymentOption: po.toJSON(),
            fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
            // Order already exists (non-CC paths run after createOrder); mint
            // under the order brand's Stax merchant (#8111).
            orderIDs: [orderID],
        }).then(r => models.PaymentOption.fromJSON(r));
    }

    function getCardBlockElements(): HTMLElement[] {
        const blocks: HTMLElement[] = [document.getElementById('card-block-1') as HTMLElement];
        $additionalCards.querySelectorAll<HTMLElement>('.payment-card-block').forEach(block => {
            blocks.push(block);
        });
        return blocks;
    }

    function getCardFieldsFromBlock(block: HTMLElement) {
        const isFirst = block.id === 'card-block-1';
        const mount = cardMounts.get(block.id);
        if (!mount) {
            throw new Error(`getCardFieldsFromBlock: no BlockChyp mount for ${block.id}`);
        }
        return {
            mount,
            expMonth: isFirst ? $expMonth1 : block.querySelector('.exp-month-select') as HTMLSelectElement,
            expYear: isFirst ? $expYear1 : block.querySelector('.exp-year-select') as HTMLSelectElement,
            amount: isFirst ? $cardAmt1 : block.querySelector('.card-amt-input') as HTMLInputElement,
            chargeState: block.querySelector('.card-charge-state') as HTMLElement,
        };
    }

    async function chargeCardsSequentially(blocks: HTMLElement[], user: models.User) {
        // Freeze every block's iframes for the duration of the charge
        // sequence so the user can't edit a later card while an earlier
        // card is processing (the pre-flight token cache wouldn't
        // reflect the new input). Thawed on decline (per-block, so the
        // user can fix the bad card) and on success (just before
        // redirect, defensive — page navigates anyway).
        freezeCardIframes();

        for (let blockIdx = 0; blockIdx < blocks.length; blockIdx++) {
            const block = blocks[blockIdx];
            const fields = getCardFieldsFromBlock(block);

            // Use the pre-tokenized card from handlePlaceOrder if present.
            // Falls back to a fresh tokenize on the retry-after-decline
            // path: handleDecline doesn't pre-populate cardTokens, so the
            // iframe is the source of truth for the new card data.
            let tokenized = cardTokens.get(block.id);
            cardTokens.delete(block.id);

            // Display position derived from DOM order \u2014 `blockIdx` is
            // 0-based on the (possibly sliced) `blocks` arg, which would
            // print "Charging card 1\u2026" while the form labels card 2 on
            // the retry-after-decline path. data-card holds the monotonic
            // mount id, not the display index, so it's also wrong.
            const displayPos = getCardBlockElements().indexOf(block) + 1;

            // Show generic charging state \u2014 BlockChyp reveals last4 in
            // the tokenize response (maskedPan), so we update with the real
            // last4 once tokenization completes.
            fields.chargeState.classList.remove('d-none');
            fields.chargeState.innerHTML = `<span class="spinner-border spinner-border-sm blue-title mr-1"></span>
                <span class="small text-muted">Charging card ${displayPos}\u2026</span>`;

            let last4 = tokenized?.lastFour ?? '';
            const chargingLabel = (name: string) => {
                $placeOrderBtn.innerHTML = '<span class="spinner-border spinner-border-sm mr-2"></span>';
                $placeOrderBtn.append(`Charging ${name}…`);
            };
            chargingLabel(last4 ? cardName(tokenized?.cardType, last4) : `Card ${displayPos}`);
            try {
                if (!tokenized) {
                    tokenized = await fields.mount.tokenize(buildTokenizeOpts(fields));
                    last4 = tokenized.lastFour ?? '';
                    chargingLabel(cardName(tokenized.cardType, last4));
                }
                // One card pays what the store credit leaves; with no credit it pays the order's balance as before.
                const singleAmount = creditApplied().gt(0) ? coverAmount().round(2) : undefined;

                const useBilling = !$billSame.checked;
                const billingCountrySel = useBilling ? $billingCountry : $shippingCountry;
                const billingStateSel = useBilling ? $billingState : $shippingState;
                const fullName = useBilling ? $billingName.value.trim() : $shippingName.value.trim();
                const addressLine1 = useBilling ? $billingAddress1.value.trim() : $shippingAddress1.value.trim();
                const addressLine2 = useBilling ? $billingAddress2.value.trim() : $shippingAddress2.value.trim();
                const city = useBilling ? $billingCity.value.trim() : $shippingCity.value.trim();
                const zip = useBilling ? $billingZip.value.trim() : $shippingZip.value.trim();
                const countryID = parseInt(billingCountrySel.value, 10);
                const stateID = parseInt(billingStateSel.value, 10);

                const paymentOption = await paymentoptions.NewPaymentOption({
                    user: user,
                    paymentOption: new models.PaymentOption({
                        fullName,
                        company: $shippingCompany.value.trim(),
                        countryID,
                        addressLine1,
                        addressLine2,
                        city,
                        stateID,
                        zip,
                        phone: await phoneInput.format(),
                        email: $email.value.trim(),
                        paymentOptionTypeID: models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT,
                    }).toJSON(),
                    paymentToken: tokenized.token,
                    creditCardFirst6: tokenized.firstSix || undefined,
                    creditCardLast4: tokenized.lastFour || undefined,
                    expireMonth: parseInt(fields.expMonth.value, 10),
                    expireYear: parseInt(fields.expYear.value, 10),
                    fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
                    // Order already exists; mint under the order brand (#8111).
                    orderIDs: [orderID],
                });

                // Charge
                let k = blockIdemKeys.get(block.id);
                if (!k) {
                    k = crypto.randomUUID();
                    blockIdemKeys.set(block.id, k);
                }

                const chargeResult = await CreateOrderPayments({
                    user: chargeUser(user),
                    orderIDs: [orderID],
                    paymentOptionID: paymentOption.key || paymentOption.id,
                    amount: splitMode && fields.amount ? parseFloat(fields.amount.value) : singleAmount?.toNumber(),
                    idempotencyKey: k,
                });

                // Show success
                // A replay can return the original committed charge after the
                // customer edited the retry input, so display the persisted
                // rows \u2014 amount AND card identity \u2014 rather than local state.
                const chargedAmount = chargeResult.orderPayments.reduce(
                    (total, orderPayment) => total.plus(orderPayment.total),
                    Big(0),
                );
                if (chargeResult.creditCardLast4) {
                    last4 = chargeResult.creditCardLast4;
                }
                const amount = DollarFormat(chargedAmount);
                const last4Display = last4 ? `\u2014 card ending \u2022\u2022\u2022\u2022${last4}` : '';
                fields.chargeState.innerHTML = `<span class="text-success small">\u2713 Charged ${amount} ${last4Display}</span>`;
                pagePaid.set(block.id, { label: last4 ? cardName(tokenized.cardType, last4) : `Card ${displayPos}`, amount: chargedAmount });
                paintSummaryPayments();

                const submittedAmount = (splitMode && fields.amount ? Big(fields.amount.value) : singleAmount ?? resumedOrderBalance ?? currentTotal).round(2);
                if (!chargedAmount.eq(submittedAmount)) {
                    // The server replayed a charge that committed before the
                    // customer edited this amount (lost-response retry, #5656).
                    // Later cards' split amounts are stale relative to what was
                    // actually taken \u2014 stop before charging them (or completing
                    // checkout) and let the customer review first.
                    for (let i = blockIdx + 1; i < blocks.length; i++) {
                        cardTokens.delete(blocks[i].id);
                    }
                    thawCardIframes(blocks.slice(blockIdx + 1));
                    handleReplayMismatch(block, chargedAmount);
                    return;
                }
            } catch (err) {
                // Empty/invalid field — paint the wrapper, no decline modal.
                // Reached on retry-after-decline, where the block re-tokenizes
                // here instead of in the pre-flight tokenizeAllCardBlocks pass.
                if (err instanceof blockchyp.TokenizeFieldError) {
                    markCardFieldInvalid(block, err.field);
                    fields.chargeState.classList.add('d-none');
                    fields.chargeState.innerHTML = '';
                    for (let i = blockIdx + 1; i < blocks.length; i++) {
                        cardTokens.delete(blocks[i].id);
                    }
                    thawCardIframes(blocks.slice(blockIdx));
                    handleDecline(block, {
                        skipAlert: true,
                        enableRetryImmediately: true,
                    });
                    return;
                }

                // A tokenize timeout retires the mount (ts/blockchyp.ts
                // unload()s frames + listener). Remount before handleDecline
                // so live fields exist for re-entry and the input handler can
                // re-arm. Without this Retry stays disabled forever.
                const mountRetired = fields.mount.isUnloaded;
                if (mountRetired) {
                    mountCardBlock(block);
                }

                // Timeout is not a decline — the card was never presented.
                // Surface the customer-ready message from blockchyp.ts rather
                // than the decline copy.
                let timeoutMsg = '';
                if (mountRetired) {
                    timeoutMsg = err instanceof Error && err.message ?
                        err.message :
                        'Card entry timed out. Please re-enter your card details and try again.';
                    fields.chargeState.innerHTML = `<span class="text-danger small">\u2717 ${timeoutMsg}</span>`;
                } else {
                    const last4Display = last4 ? `\u2014 card ending \u2022\u2022\u2022\u2022${last4}` : '';
                    fields.chargeState.innerHTML = `<span class="text-danger small">\u2717 Declined ${last4Display}</span>`;
                }

                // Drop pre-tokenized cards for blocks we never reached.
                // BlockChyp iframes stay interactive during the decline
                // alert, so the customer can edit a later card before
                // hitting Retry \u2014 clearing the cache forces those blocks
                // to re-tokenize from current iframe state on retry.
                for (let i = blockIdx + 1; i < blocks.length; i++) {
                    cardTokens.delete(blocks[i].id);
                }

                // Thaw the declined block + any unreached blocks so the
                // customer can fix the bad card (and edit cards we never
                // got to) before retrying. Successfully charged blocks
                // earlier in the loop stay frozen — we've already taken
                // their money, no reason to let the customer "edit" them.
                // Mirrors handleDecline only re-enabling native inputs on
                // the declined block.
                thawCardIframes(blocks.slice(blockIdx));

                if (mountRetired) {
                    handleDecline(block, {
                        alertHtml: `<strong>${timeoutMsg}</strong>
                            Your order has been saved &mdash; previously charged amounts will not be re-charged.`,
                        // Fresh iframes after remount; enable Retry now rather
                        // than waiting on an input event that may not re-fire
                        // until the customer types (and nothing could re-enable
                        // it before the remount).
                        enableRetryImmediately: true,
                    });
                } else {
                    handleDecline(block);
                }
                return;
            }
        }

        // All cards successful \u2014 keep iframes frozen through
        // checkout-complete so the user can't edit fields while the
        // form is still in a "Placing Order" state. Thaw on failure so
        // the retry-after-decline path (which calls this with `void`)
        // doesn't leave iframes locked when checkout-complete throws.
        try {
            await finishOrder(user);
        } catch (err) {
            failFinish(err, user);
        }
    }

    // finishOrder runs once every card is paid: the store credit, then the order's completion.
    async function finishOrder(user: models.User) {
        await takeCredit(user);
        await axios.post(`/orders/${orderID}/checkout-complete`);
        await redirectToConfirmation(orderKey);
    }

    // The cards are paid, so trying again only finishes the order; a credit attempt keeps its idempotency key.
    function failFinish(err: unknown, user: models.User) {
        ShowError(err);
        chargeSequenceActive = false;
        setCreditStatus('not-taken');
        $placeOrderBtn.disabled = false;
        $placeOrderBtn.textContent = 'Try Again';
        $placeOrderBtn.onclick = async () => {
            $placeOrderBtn.disabled = true;
            $placeOrderBtn.innerHTML = '<span class="spinner-border spinner-border-sm mr-2"></span>Placing Order\u2026';
            chargeSequenceActive = true;
            setCreditStatus('waiting');
            try {
                await finishOrder(user);
            } catch (retryErr) {
                failFinish(retryErr, user);
            }
        };
    }

    // A lost-response retry replayed this block's original committed charge,
    // which no longer matches the edited amount (#5656). The money moved at
    // the original amount \u2014 the charged block stays frozen (its state line
    // already shows what was actually taken), later blocks' native fields
    // unlock so the customer can actually fix the remaining split, and
    // Continue resumes from the NEXT block.
    function handleReplayMismatch(chargedBlock: HTMLElement, chargedAmount: InstanceType<typeof Big>) {
        const laterBlocks = () => {
            const allBlocks = getCardBlockElements();
            return allBlocks.slice(allBlocks.indexOf(chargedBlock) + 1);
        };
        const setLaterFieldsDisabled = (disabled: boolean) => {
            for (const laterBlock of laterBlocks()) {
                const fields = getCardFieldsFromBlock(laterBlock);
                fields.expMonth.disabled = disabled;
                fields.expYear.disabled = disabled;
                if (splitMode && fields.amount) {
                    fields.amount.disabled = disabled;
                }
            }
        };
        setLaterFieldsDisabled(false);

        // The charged block's input still shows the post-edit amount, but the
        // money moved at the ORIGINAL amount \u2014 correct the input so the split
        // math (and validateSplitAmounts below) reflects what was actually
        // taken, otherwise an underpaid checkout could complete.
        const chargedFields = getCardFieldsFromBlock(chargedBlock);
        if (splitMode && chargedFields.amount) {
            chargedFields.amount.value = chargedAmount.toFixed(2);
        }
        validateSplitAmounts();

        $declineAlert.innerHTML = `<strong>This card was already charged ${DollarFormat(chargedAmount)}.</strong>
            An earlier attempt went through at the original amount. Review the remaining card
            amounts above, then continue \u2014 already-charged cards will not be re-charged.`;
        $declineAlert.classList.remove('d-none');

        $placeOrderBtn.disabled = false;
        $placeOrderBtn.textContent = 'Continue';
        setCreditStatus('not-taken');

        $placeOrderBtn.onclick = async () => {
            if (!validateSplitAmounts()) {
                return;
            }
            setLaterFieldsDisabled(true);
            setCreditStatus('waiting');
            $placeOrderBtn.disabled = true;
            $placeOrderBtn.innerHTML = '<span class="spinner-border spinner-border-sm mr-2"></span>Placing Order\u2026';
            $declineAlert.classList.add('d-none');

            const user = userDetails();
            user.phone = await phoneInput.format();
            user.pageRequestID = await GetPageRequestID();
            void chargeCardsSequentially(laterBlocks(), user);
        };
    }

    // Retry wiring after a failed charge (or tokenize) for one block. Does not
    // touch blockIdemKeys — those stay so a lost-response retry replays the
    // same charge attempt (#5656). opts overrides the default decline copy and
    // whether Retry waits for a fresh iframe input event.
    function handleDecline(declinedBlock: HTMLElement, opts?: {
        alertHtml?: string;
        enableRetryImmediately?: boolean;
        // Field-scoped tokenize errors use inline is-invalid instead of the
        // decline banner — keep Retry wiring without the alert.
        skipAlert?: boolean;
    }) {
        // Re-enable ONLY the declined card block's native fields. The
        // PAN/CVC iframes are always interactive (no per-block disable
        // hook), so the user can edit them immediately \u2014 we just need
        // to detect the edit to re-enable the Retry button (unless
        // enableRetryImmediately — e.g. post-timeout remount).
        const fields = getCardFieldsFromBlock(declinedBlock);
        fields.expMonth.disabled = false;
        fields.expYear.disabled = false;
        if (splitMode && fields.amount) {
            fields.amount.disabled = false;
        }

        // Show alert (restore the default decline copy \u2014 a replay-mismatch
        // or timeout alert earlier in this page life may have overwritten it)
        if (!opts?.skipAlert) {
            $declineAlert.innerHTML = opts?.alertHtml ?? `<strong>One card was declined.</strong>
            Update the card details above and retry. Your order has been saved &mdash;
            previously charged amounts will not be re-charged.`;
            $declineAlert.classList.remove('d-none');
        }

        // Change button to retry
        $placeOrderBtn.disabled = !opts?.enableRetryImmediately;
        $placeOrderBtn.textContent = 'Try Again';
        setCreditStatus('not-taken');

        // Enable button when the user re-touches PAN or CVC inside the
        // BlockChyp iframe. cardType fires while typing; fieldEvent on
        // focus/blur (also fires under Playwright .fill). validate is
        // blur-only, so both signals drive the retry handler.
        const enableRetry = () => {
            $placeOrderBtn.disabled = false;
            cardInputHandlers.delete(declinedBlock.id);
        };
        if (opts?.enableRetryImmediately) {
            cardInputHandlers.delete(declinedBlock.id);
        } else {
            cardInputHandlers.set(declinedBlock.id, enableRetry);
        }

        // On retry click
        $placeOrderBtn.onclick = async () => {
            // Re-disable native inputs
            fields.expMonth.disabled = true;
            fields.expYear.disabled = true;
            if (splitMode && fields.amount) {
                fields.amount.disabled = true;
            }

            $placeOrderBtn.disabled = true;
            $placeOrderBtn.innerHTML = '<span class="spinner-border spinner-border-sm mr-2"></span>Placing Order\u2026';
            $declineAlert.classList.add('d-none');
            setCreditStatus('waiting');
            clearCardFieldInvalid(declinedBlock);
            cardInputHandlers.delete(declinedBlock.id);

            // Resume charge from the declined card forward
            const allBlocks = getCardBlockElements();
            const startIndex = allBlocks.indexOf(declinedBlock);
            const remaining = allBlocks.slice(startIndex);

            const user = userDetails();
            user.phone = await phoneInput.format();
            user.pageRequestID = await GetPageRequestID();

            void chargeCardsSequentially(remaining, user);
        };
    }

    // Once an order exists (same-session decline retry or cross-reload resume),
    // fields baked into it at creation stay frozen so edits can't silently
    // diverge from the server order. Payment + verify + place-order stay live.
    function isFrozenOnResume(el: Element): boolean {
        if (!orderID) {
            return false;
        }
        const $paymentCard = document.getElementById('payment-card');
        if ($paymentCard?.contains(el)) {
            return false;
        }
        if (el.id === 'verify' || el.id === 'place-order-btn') {
            return false;
        }
        return true;
    }

    function reEnableForm(fieldset: NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>) {
        fieldset.forEach(el => {
            if (isFrozenOnResume(el)) {
                return;
            }
            el.disabled = false;
        });
        $placeOrderBtn.disabled = false;
        $placeOrderBtn.textContent = 'Place order';
        // Stopped before any payment, the credit is back to what the customer set; after one, it waits its turn.
        chargeSequenceActive = false;
        if (credit) {
            credit.status = paymentTried() ? 'not-taken' : 'editing';
        }
        paintCredit();
        paintSummaryPayments();
    }

    async function applyResumableOrder(resumableOrderInit: models.IResumableOrder | undefined) {
        if (!resumableOrderInit) {
            return;
        }

        const resumableOrder = new models.ResumableOrder(resumableOrderInit);
        const shippingOption = resumableOrder.shippingOption;
        if (!shippingOption.id) {
            return;
        }

        const shippingAddress = shippingOption.address;
        if (!shippingAddress) {
            return;
        }

        orderID = resumableOrder.orderID;
        orderKey = resumableOrder.key;
        resumedOrderBalance = resumableOrder.balance;
        for (const payment of resumableOrder.payments) {
            if (!payment.total.gt(0)) {
                continue;
            }
            let label = 'Payment';
            if (payment.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE) {
                label = 'Store Credit';
            } else if (payment.creditCardLast4) {
                // The card's first six digits name its brand, so the label matches the one shown before the reload.
                label = cardName(Issuer(payment.creditCardFirst6), String(payment.creditCardLast4));
            }
            resumedPaid.push({ label, amount: payment.total });
        }

        const $resumeInvoice = document.getElementById('resume-invoice');
        if ($resumeInvoice) {
            $resumeInvoice.textContent = String(resumableOrder.invoiceNumber);
        }
        document.getElementById('resume-alert')?.classList.remove('d-none');

        $checkoutForm.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>(
            'input, select, textarea, button',
        ).forEach(el => {
            if (isFrozenOnResume(el)) {
                el.disabled = true;
            }
        });

        $shippingName.value = shippingAddress.fullName || '';
        $shippingCompany.value = shippingAddress.organization || '';
        $shippingAddress1.value = shippingAddress.addressLine1 || '';
        $shippingAddress2.value = shippingAddress.addressLine2 || '';
        $shippingCity.value = shippingAddress.city || '';
        $shippingZip.value = shippingAddress.postalCode || '';
        $email.value = shippingAddress.emailAddress?.email || '';
        if (shippingAddress.phoneNumber?.phone) {
            phoneInput.iti.setNumber(shippingAddress.phoneNumber.phone);
        }

        await shippingSelectsReady;
        $shippingCountry.value = String(shippingAddress.countryID);
        $shippingCountry.dispatchEvent(new Event('change'));
        const stateValue = String(shippingAddress.stateID);
        const deadline = Date.now() + 2000;
        while (Date.now() < deadline) {
            if ($shippingState.querySelector(`option[value="${stateValue}"]`)) {
                $shippingState.value = stateValue;
                break;
            }
            await new Promise(r => setTimeout(r, 50));
        }

        $splitBtn.classList.add('d-none');

        const userTaxExemption = resumableOrder.userTaxExemption;
        if (userTaxExemption?.id) {
            // The resume-time refresh reads the selector, so the recorded exemption must be back before it runs, else the total shows tax the order never charged.
            $taxExemption.value = exemptTypeFor(userTaxExemption.userTaxExemptionReasonID ?? models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_ZERO);
            $exemptNumber.value = userTaxExemption.number || '';
            if (userTaxExemption.stateID) {
                const exemptStateValue = String(userTaxExemption.stateID);
                const exemptDeadline = Date.now() + 2000;
                while (Date.now() < exemptDeadline) {
                    if ($exemptState.querySelector(`option[value="${exemptStateValue}"]`)) {
                        $exemptState.value = exemptStateValue;
                        break;
                    }
                    await new Promise(r => setTimeout(r, 50));
                }
            }
            $taxExemption.dispatchEvent(new Event('change'));
            $exemptionFields.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>(
                'input, select, textarea, button',
            ).forEach(el => {
                if (isFrozenOnResume(el)) {
                    el.disabled = true;
                }
            });
        }

        // The restored address is complete but was set by assignment, not by the
        // events fetchTax listens to, and orderID is now set, so this is the fetch
        // that swaps the live-balance preview for the resumed order's recorded
        // available-discount spend (and its tax).
        fetchTax();
    }

    async function redirectToConfirmation(orderKey: string) {
        // Clear the placed-order cart so /cart shows empty next visit and the nav
        // badge resets. Server-side carts are converted to orders on checkout, but
        // the client still holds the now-stale cartID until we forget it.
        await smgCarts.ForgetCart().catch(() => undefined);
        carts.SetCartCount(0);
        window.location.href = Relref('/order-detail') + '?key=' + encodeURIComponent(orderKey);
    }

    function getExemptReasonID(type: string): models.UserTaxExemptionReasonID {
        switch (type) {
        case 'government': return models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_GOVERNMENT_EXEMPTION;
        case 'wholesale': return models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_WHOLESALE_OR_RETAIL_EXEMPTION;
        case 'entity': return models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_ENTITY_EXEMPT;
        default: return models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_ZERO;
        }
    }

    function exemptTypeFor(reasonID: models.UserTaxExemptionReasonID): 'government' | 'wholesale' | 'entity' | 'none' {
        switch (reasonID) {
        case models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_GOVERNMENT_EXEMPTION: return 'government';
        case models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_WHOLESALE_OR_RETAIL_EXEMPTION: return 'wholesale';
        case models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_ENTITY_EXEMPT: return 'entity';
        default: return 'none';
        }
    }
}
